/*
  AReviews custom behavior — replicates the Linda Casa (Yampi) reference layout
  on top of the AReviews app's markup.

  Three jobs:
  1. Title row: rebuild as 3 product-anchor buttons (rating / chat count / link).
  2. Reviews section:
     - Empty state: full-width cream card.
     - With reviews: 2-col layout (sticky summary | masonry 3-col cards).
  3. Stylesheet promotion: keep our <link> as the LAST <link>/<style> in <body>
     so we win cascade ties against AReviews' XHR-injected <style> blocks.

  ============================================================================
  THEME CONTRACT — dependencies on Horizon theme (assets/base.css, layout
  templates, etc.). When Horizon updates, audit the items below first.
  ============================================================================

  CSS classes consumed:
    - `dialog-modal`              styles for our <dialog> wrappers (write,
                                  success, media viewer). Provides the modal
                                  chrome (backdrop, scroll-lock awareness).
    - `color-scheme-1`            applies the active scheme tokens to dialogs
                                  so they don't inherit transparent backgrounds.
    - `pdp-mobile-slot`           marker on Liquid blocks moved into the mobile
                                  reorder slot (blocks/_pdp-mobile-header-slot.liquid).
                                  Used by applyMobileDedup() to identify which
                                  AreviewsTitleRating instance is the mobile
                                  one and dedupe at the breakpoint.
    - `shopify-section`           wrapper around every Shopify section. Used
                                  by pillScrollHandler() to scroll to the
                                  section root rather than #az_reviews itself
                                  (avoids landing under the sticky header).

  HTML attributes consumed:
    - `scroll-lock`               attribute toggled by Horizon's body-scroll
                                  utility. We mirror it on our <dialog>s so
                                  Horizon's scroll-lock observer picks them up.

  CSS custom properties consumed (defined in snippets/theme-styles-variables):
    - `--icon-stroke-width`       used in our SVG strokes to match Horizon
                                  icon weight in buttons/anchors.
    - `--color-foreground`        text + star fill in skeleton/empty states.
    - `--color-foreground-rgb`    used with rgb(var(...) / opacity) for
                                  subdued backgrounds.

  Files this code expects to exist:
    - assets/areviews-custom.css       loaded last to win cascade vs. AReviews.
    - snippets/areviews-critical-css   inline FOUC guard (rendered in <head>
                                       by layout/theme.liquid). Defines the
                                       `.aRev-seeded` and `.aRev-rebuilt`
                                       reveal contracts that JS satisfies.

  Selectors AReviews exposes (subject to drift across extension versions —
  triple-check on every AReviews app update):
    - `.areviews_main_header_container`        title pill outer wrapper.
    - `.areviews_header_stars`                 native star list (we hide).
    - `[data-block-handle="AreviewsTitleRating"]`   old extension version.
    - `[id*="areviews_title_rating"]`          new extension version (no
                                               data-block-handle attr).
    - `#az_reviews`                            reviews section root.
    - `#totalReviews`                          rating count (presence used as
                                               XHR-finished signal).
    - `.areviews_slide_image_details`          per-review card container.
    - `#enter_reviews`                         submit form.
    - `#areviews_form_stars`                   inline star-picker widget.
    - `.areviews_product_item`                 collection-card stars host.

  ============================================================================
*/
(function () {
  const titleContainerSel = '.areviews_main_header_container';
  const reviewsRootSel = '#az_reviews';
  const customStylesheetSel = 'link[href*="areviews-custom.css"]';

    /* ===== CAMADA DE COMPATIBILIDADE MULTI-TEMA ===== */
  const AREV_SEL = {
    count: [
      () => {
        const el = document.querySelector('#totalReviews');
        if (!el) return null;
        const v = parseInt(el.textContent || '0', 10);
        return Number.isFinite(v) ? v : null;
      },
      () => {
        const el = document.querySelector('#az_reviews [data-areviews-count]');
        if (!el) return null;
        const v = parseInt(el.dataset.areviewsCount, 10);
        return Number.isFinite(v) ? v : null;
      },
      () => {
        const el = document.querySelector('#az_reviews [data-reviews-num]');
        if (!el || el.dataset.reviewsNum === '') return null;
        const v = parseInt(el.dataset.reviewsNum, 10);
        return Number.isFinite(v) ? v : null;
      },
    ],
    average: [
      () => {
        const el = document.querySelector('#az_reviews .AreviewsHeader5-ratingNumber');
        if (!el) return null;
        const v = parseFloat((el.textContent || '').replace(',', '.'));
        return Number.isFinite(v) && v > 0 ? v : null;
      },
      () => {
        const el = document.querySelector('#az_reviews [data-areviews-average]');
        if (!el) return null;
        const v = parseFloat(String(el.dataset.areviewsAverage).replace(',', '.'));
        return Number.isFinite(v) && v > 0 ? v : null;
      },
    ],
    dist: [
      () => {
        const ws = document.querySelectorAll('#az_reviews .AreviewsHeader5-ratingBarWrapper');
        if (!ws.length) return null;
        const r = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
        ws.forEach((w) => {
          const stars = parseInt(w.getAttribute('data-rating') || '0', 10);
          const m = w.querySelector('.AreviewsHeader5-ratingBarFill')
            ?.getAttribute('style')?.match(/width:\s*([\d.]+)/);
          if (stars >= 1 && stars <= 5 && m) r[stars] = parseFloat(m[1]);
        });
        return r;
      },
      () => {
        const rows = document.querySelectorAll('#az_reviews [data-rating]');
        if (!rows.length) return null;
        const r = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
        let achou = false;
        rows.forEach((row) => {
          const stars = parseInt(row.getAttribute('data-rating') || '0', 10);
          if (!(stars >= 1 && stars <= 5)) return;
          const fill = [...row.querySelectorAll('[style*="width"]')]
            .find((e) => /width:\s*[\d.]+%/.test(e.getAttribute('style') || ''));
          if (!fill) return;
          r[stars] = parseFloat(fill.getAttribute('style').match(/width:\s*([\d.]+)/)[1]);
          achou = true;
        });
        return achou ? r : null;
      },
    ],
    bodyEl: [
      '#reviewsBody',
      '#az_reviews #wrapper',
      '#az_reviews .themes_container',
      '#az_reviews .main_reviews_container',
    ],
    cards: [
      '.callout.areviews_slide_image_details',
      '.areviews_slide_image_details',
      '.areviews_review_card',
    ],
  };

  function arevResolve(kind) {
    const chain = AREV_SEL[kind];
    for (let i = 0; i < chain.length; i++) {
      const v = chain[i]();
      if (v !== null && v !== undefined) {
        dlog('resolve(' + kind + ') → variante #' + i + ' =', v);
        return v;
      }
    }
    dwarn('resolve(' + kind + ') → NENHUMA variante casou');
    return null;
  }

  function arevResolveEl(kind, root) {
    const scope = root || document;
    for (const sel of AREV_SEL[kind]) {
      const el = scope.querySelector(sel);
      if (el) { dlog('resolveEl(' + kind + ') → "' + sel + '"'); return el; }
    }
    return null;
  }

  function arevResolveAll(kind, root) {
    const scope = root || document;
    for (const sel of AREV_SEL[kind]) {
      const els = scope.querySelectorAll(sel);
      if (els.length) { dlog('resolveAll(' + kind + ') → "' + sel + '" (' + els.length + ')'); return [...els]; }
    }
    return [];
  }
  /* ===== FIM DA CAMADA DE COMPATIBILIDADE ===== */

  /* AReviews title-rating block selector.
     Old extension version exposed [data-block-handle="AreviewsTitleRating"];
     new version dropped that attribute and only the Shopify app-block id
     remains (id*="areviews_title_rating"). Match BOTH so this works across
     extension versions without per-shop drift (the source of a daha-only
     bug where seeding never ran and the populated container was hidden by
     the .aRev-seeded CSS guard). */
  const AREV_TITLE_BLOCK_SEL =
    '[data-block-handle="AreviewsTitleRating"], [id*="areviews_title_rating"]';

  /* Fallback deadline for the `#totalReviews` data-arrival gate in
     applyTitleBadge. After this many ms from script-eval, rebuild the
     pill with whatever we have (likely empty-state "Novo!"), so an
     AReviews outage doesn't leave the skeleton pulsing forever. */
  const AREV_DATA_TIMEOUT_MS = 10000;
  const aRevDataDeadline = performance.now() + AREV_DATA_TIMEOUT_MS;

  /* -----------------------------------------------------------
     Debug instrumentation — opt-in.

     Activation (any one):
       - URL query: append `?arev-debug=1` (auto-persists to localStorage so it
         survives navigation; remove with `?arev-debug=0`)
       - DevTools: `localStorage.aReviewsDebug = '1'` (then reload)
       - Runtime: `window.__AREVIEWS_DEBUG__ = true` (live, no persist)

     When enabled, prints `[areviews-custom]` snapshots at every gate of the
     render pipeline so we can see why the skeleton hangs (most often:
     `#totalReviews` never populates because the AReviews XHR didn't land).

     Zero-cost when disabled — `dlog`/`debugSnapshot` early-return.
     ----------------------------------------------------------- */
  const AREV_DEBUG = (() => {
    try {
      const url = new URL(window.location.href);
      const param = url.searchParams.get('arev-debug');
      if (param === '1') {
        try { localStorage.setItem('aReviewsDebug', '1'); } catch (_) {}
        return true;
      }
      if (param === '0') {
        try { localStorage.removeItem('aReviewsDebug'); } catch (_) {}
        return false;
      }
      if (window.__AREVIEWS_DEBUG__) return true;
      try { return localStorage.getItem('aReviewsDebug') === '1'; } catch (_) { return false; }
    } catch (_) { return false; }
  })();

  function dlog(...args) {
    if (!AREV_DEBUG) return;
    try { console.log('[areviews-custom]', ...args); } catch (_) {}
  }
  function dwarn(...args) {
    if (!AREV_DEBUG) return;
    try { console.warn('[areviews-custom]', ...args); } catch (_) {}
  }

  /* DOM/state snapshot — captures every signal the render pipeline depends on,
     so a single line in the console answers "why is the skeleton stuck?". */
  function debugSnapshot(label) {
    if (!AREV_DEBUG) return;
    /* Wrapped because some fields (sourceAccumulator, getReviewsCount) reference
       bindings declared later in this IIFE — calling debugSnapshot('script-eval')
       before those declarations would otherwise hit the TDZ. */
    let snap;
    try {
      const az = document.querySelector(reviewsRootSel);
      const totalReviewsEl = document.getElementById('totalReviews');
      const reviewsBody = document.getElementById('reviewsBody');
      const totalText = totalReviewsEl ? (totalReviewsEl.textContent || '').trim() : null;
      const sourcesInBody = reviewsBody
        ? reviewsBody.querySelectorAll('.callout.areviews_slide_image_details').length
        : 0;
      const ratingNumberEl = az?.querySelector('.AreviewsHeader5-ratingNumber');
      let reviewsCount = 'n/a';
      try { reviewsCount = getReviewsCount(); } catch (_) {}
      let accumulatorLen = 'n/a';
      try { accumulatorLen = sourceAccumulator.length; } catch (_) {}
      snap = {
        label,
        tMs: Math.round(performance.now()),
        deadlinePassed: performance.now() > aRevDataDeadline,
        readyState: document.readyState,
        az: !!az,
        azProductId: az?.dataset?.product_id || null,
        azChildren: az?.children?.length ?? 0,
        azClass: az?.className ?? null,
        totalReviewsEl: !!totalReviewsEl,
        totalReviewsText: totalText,
        reviewsCount,
        ratingNumberText: ratingNumberEl ? (ratingNumberEl.textContent || '').trim() : null,
        reviewsBodyEl: !!reviewsBody,
        sourcesInBody,
        accumulatorLen,
        holder: !!document.querySelector(reviewsRootSel + ' .aRev-holder'),
        holderLoading: !!document.querySelector(reviewsRootSel + ' .aRev-holder--loading'),
        titleBlocks: document.querySelectorAll(AREV_TITLE_BLOCK_SEL).length,
        titleSeeded: document.querySelectorAll('.areviews_main_header_container.aRev-seeded').length,
        titleRebuilt: document.querySelectorAll('.areviews_main_header_container.aRev-rebuilt').length,
        windowCustomerIdType: typeof window.customer_id,
        hasAreviewsLoadMore: typeof window.areviews_load_more,
        hasLoadMoreReviews: typeof window.loadMoreReviews,
      };
    } catch (e) {
      snap = { label, error: String(e) };
    }
    try { console.log('[areviews-custom][snap]', snap); } catch (_) {}
  }

  if (AREV_DEBUG) {
    try {
      console.log(
        '%c[areviews-custom] DEBUG ACTIVE',
        'background:#222;color:#FFCC00;padding:2px 6px;border-radius:3px;font-weight:bold;',
        '— logging render-pipeline state. Disable with ?arev-debug=0 or `delete localStorage.aReviewsDebug`.'
      );
    } catch (_) {}
    debugSnapshot('script-eval');
  }

  /* -----------------------------------------------------------
     Mobile dedup — JS path with inline-style triple defense

     Earlier iterations relied on CSS `:has()` + a class rule inside a
     `@media (max-width: 749px)` block. Both approaches failed on at
     least one real mobile browser in the wild (user report — problem
     reproduced only on the physical device, not Playwright's Chromium
     at mobile viewport). Possible causes: stale CSS cache, `:has()`
     shipping differently across WebKit forks, or a CSS parse failure
     we can't reproduce.

     Nuclear solution: use JS to set `display: none !important` DIRECTLY
     on the non-mobile-slot app block via the element's inline
     `style.setProperty('display', 'none', 'important')` — bypasses the
     stylesheet entirely, can't be defeated by cache or `:has()` support.
     Inline !important beats every external stylesheet rule except those
     with equal !important priority AND higher specificity (of which
     there are none targeting a plain block).

     Also sync when viewport rotation crosses the breakpoint — the
     change listener on `matchMedia` handles both directions.

     Idempotent: re-runs on every MutationObserver tick + viewport
     change. Cost: one querySelector per tick + small forEach.
     ----------------------------------------------------------- */
  const mobileMQL = window.matchMedia('(max-width: 749px)');

  function applyMobileDedup() {
    const blocks = document.querySelectorAll(AREV_TITLE_BLOCK_SEL);
    if (blocks.length < 2) return;
    let hasMobileSlotInstance = false;
    blocks.forEach((b) => { if (b.closest('.pdp-mobile-slot')) hasMobileSlotInstance = true; });
    if (!hasMobileSlotInstance) return;

    const isMobileViewport = mobileMQL.matches;
    blocks.forEach((b) => {
      const inMobileSlot = !!b.closest('.pdp-mobile-slot');
      if (inMobileSlot) {
        // Merchant moved this block INTO a mobile slot since last run
        // (theme-editor reorder). Clean up our marker + inline style so
        // the block reverts to default visibility.
        b.classList.remove('aRev-non-mobile-instance');
        b.style.removeProperty('display');
        return;
      }
      b.classList.add('aRev-non-mobile-instance');
      if (isMobileViewport) {
        b.style.setProperty('display', 'none', 'important');
      } else {
        b.style.removeProperty('display');
      }
    });
  }

  // Run immediately at script-eval — blocks are server-rendered so they
  // exist even before DOMContentLoaded. This wins the race against paint
  // on any browser where our <script> evaluates before the layout pass.
  applyMobileDedup();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyMobileDedup, { once: true });
  }
  // React to viewport crossings (rotation, dev-tools resize). Use the
  // addEventListener signature; older Safari 13- uses the deprecated
  // `addListener` — the try/catch below handles both.
  try {
    mobileMQL.addEventListener('change', applyMobileDedup);
  } catch (_e) {
    if (mobileMQL.addListener) mobileMQL.addListener(applyMobileDedup);
  }

  /* -----------------------------------------------------------
     Inline SVG icons — copied verbatim from the reference theme
     so the visual style matches 1:1.
     ----------------------------------------------------------- */
  const STAR_PATH =
    'M7.13557 10.5846C6.92869 10.437 6.64896 10.437 6.44208 10.5846L3.26702 12.8498C3.11469 12.9585 ' +
    '2.91895 12.9892 2.73983 12.9327C2.42896 12.8345 2.25773 12.5067 2.35738 12.2005L3.55886 8.50882C3.63805 ' +
    '8.26549 3.54691 7.99973 3.33412 7.85355L0.252948 5.73677C0.0944532 5.62789 0 5.44957 0 5.25923C0 ' +
    '4.93768 0.264648 4.67701 0.591107 4.67701H4.34417C4.59811 4.67701 4.82368 4.51725 4.90452 4.28014L6.22848 ' +
    '0.397028C6.28741 0.224174 6.42516 0.0884988 6.60065 0.03045C6.91012 -0.0719175 7.24525 0.0922051 7.34918 ' +
    '0.397028L8.67313 4.28014C8.75398 4.51725 8.97954 4.67701 9.23349 4.67701H12.9866C13.1798 4.67701 ' +
    '13.3608 4.77004 13.4714 4.92616C13.6581 5.18989 13.5925 5.55282 13.3247 5.73677L10.2435 7.85355C10.0308 ' +
    '7.99973 9.93961 8.26549 10.0188 8.50882L11.2203 12.2005C11.2777 12.3769 11.2464 12.5697 11.1361 ' +
    '12.7198C10.9446 12.9802 10.575 13.0384 10.3106 12.8498L7.13557 10.5846Z';

  function starSvg(size, filled) {
    const fill = filled ? '#FFCC00' : '#cfcfcf';
    return (
      `<svg width="${size}" height="${size}" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg" class="aRev-star">` +
      `<path d="${STAR_PATH}" fill="${fill}"></path>` +
      '</svg>'
    );
  }

  /* -----------------------------------------------------------
     Stylesheet promotion — moves our <link> to end of <body>.
     Cascade rule: when specificity is equal, the LAST declaration
     in source order wins. Moving our link below the app's <style>
     blocks (which are appended into <body> by the XHR response)
     guarantees our rules take precedence on every re-render.
     ----------------------------------------------------------- */
  function promoteCustomStylesheet() {
    const link = document.querySelector(customStylesheetSel);
    if (!link) return;
    if (link === document.body.lastElementChild) return;
    document.body.appendChild(link);
  }

  function getAverageRating() {
    return arevResolve('average');
  }

  function getReviewsCount() {
    return arevResolve('count');
  }

  function getRatingDistribution() {
    return arevResolve('dist') || { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  }

  /* The AReviews app's "write review" form (#enter_reviews) lives INSIDE #az_reviews,
     so our `aRev-empty` / `aRev-rebuilt` hide-everything rules also hide the form.
     When the user clicks our "Avaliar produto" button we need to (a) expose the app
     so the form can become visible, then (b) trigger the app's own click handler. */
  /* -----------------------------------------------------------
     Write-review modal — follows the EXACT Horizon dialog pattern:
       <dialog-component> (web component, defined in assets/dialog.js)
         <dialog ref="dialog" class="X__content dialog-modal color-scheme-1" scroll-lock aria-labelledby>
           <div class="X__inner">
             <div class="X__header">
               <h2 class="X__heading">…</h2>
               <button class="button button-unstyled close-button X__close">×</button>
             </div>
             <div class="X__body">…form…</div>
             <div class="X__footer">…submit…</div>
           </div>
         </dialog>
       </dialog-component>

     The form (#enter_reviews) is moved into the body and decorated to
     match the Linda Casa visual layout (uppercase labels, char counter,
     image slots, rules, full-width submit). Submit is proxied to the
     app's native button so AReviews handles validation/upload/POST.
     ----------------------------------------------------------- */
  const WRITE_DIALOG_ID = 'aRev-write-dialog';
  const SUCCESS_DIALOG_ID = 'aRev-success-dialog';
  const MEDIA_VIEWER_ID = 'aRev-media-viewer';
  const SUBMIT_URL = '/apps/aliexpress_reviews/store_reviews';
  const CLOSE_X_SVG =
    '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M12 2L2 12" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round" stroke-linejoin="round"></path>' +
    '<path d="M12 12L2 2" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round" stroke-linejoin="round"></path>' +
    '</svg>';
  const CHECK_CIRCLE_SVG =
    '<svg viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M14.999 4C21.0737 4 25.9988 8.92439 25.999 14.999C25.999 21.0737 21.0746 25.9988 15 25.999C8.9253 25.999 4.00026 21.0746 4 15C4 8.9253 8.92438 4.00026 14.999 4ZM14.999 6C10.029 6.00026 6 10.0299 6 15C6.00026 19.9701 10.0299 23.999 15 23.999C19.9699 23.9988 23.9988 19.9699 23.999 15C23.999 10.0297 19.9693 6 14.999 6ZM19.625 13.0137L14.7891 18.0605C14.5722 18.2869 14.2724 18.415 13.959 18.415C13.6456 18.415 13.3457 18.2868 13.1289 18.0605L10.373 15.1855L12.0342 13.5938L13.959 15.6025L17.9639 11.4229L19.625 13.0137Z" fill="currentColor"></path>' +
    '</svg>';

  /* Focus management note: native <dialog showModal()> traps focus on
     Chrome 92+, Firefox 98+, and Safari 15.4+. Safari 15.3 and earlier
     (~0.5% of mobile traffic in 2026 — declining) lack the trap; user
     can Tab out of the modal. We intentionally do NOT install a manual
     focus trap because (a) the cost is real (keydown listener + focus
     restoration tracking) and (b) the platform is converging quickly.
     Re-evaluate if Safari 15.3- usage rises above 1%. */
  function ensureWriteDialog() {
    let wrapper = document.getElementById(WRITE_DIALOG_ID);
    if (wrapper) return wrapper;

    wrapper = document.createElement('dialog-component');
    wrapper.id = WRITE_DIALOG_ID;
    wrapper.className = 'aRev-write-dialog';
    wrapper.innerHTML =
      '<dialog ref="dialog" class="aRev-write-dialog__content dialog-modal color-scheme-1" scroll-lock aria-labelledby="aRev-write-dialog-title">' +
      '<div class="aRev-write-dialog__inner">' +
      '<div class="aRev-write-dialog__header">' +
      '<h2 id="aRev-write-dialog-title" class="aRev-write-dialog__heading">Avalie o produto</h2>' +
      `<button type="button" class="button-unstyled aRev-write-dialog__close" aria-label="Fechar caixa de diálogo">${CLOSE_X_SVG}</button>` +
      '</div>' +
      '<div class="aRev-write-dialog__body"></div>' +
      '<div class="aRev-write-dialog__footer">' +
      '<button type="button" class="aRev-write-dialog__submit">Enviar avaliação</button>' +
      '</div>' +
      '</div>' +
      '</dialog>';
    document.body.appendChild(wrapper);

    const dialog = wrapper.querySelector('dialog');
    const closeBtn = wrapper.querySelector('.aRev-write-dialog__close');
    const submitProxy = wrapper.querySelector('.aRev-write-dialog__submit');

    closeBtn.addEventListener('click', () => closeWriteDialog(wrapper));
    dialog.addEventListener('close', returnFormToOrigin);

    /* Proxy our footer button → AReviews's real submit. Validation runs FIRST:
         1. Star rating > 0 (our custom widget — HTML5 can't validate buttons)
         2. Native checkValidity() for all `required` inputs the app marked up
       If either fails we bail without triggering the AReviews submit, so the
       user gets the browser's native tooltips (and a shake on the stars row)
       — no spinner, no XHR, no false "I'm working" feedback.

       The loading state itself is NOT activated here; it's activated when the
       XHR actually opens (see setupSubmissionInterception → arev:submission-
       start). That guarantees the spinner only appears when the request is
       genuinely in flight, never when validation kept the click from
       producing a request. */
    submitProxy.addEventListener('click', () => {
      if (submitProxy.dataset.aRevBusy === '1') return;
      const form = document.querySelector('#enter_reviews');
      if (!form) return;

      const ratingInput = form.querySelector('#comment_rating_ra');
      const rating = ratingInput ? parseInt(ratingInput.value, 10) || 0 : 0;
      if (rating === 0) {
        flashStarsInvalid(form);
        return;
      }

      if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
        if (typeof form.reportValidity === 'function') form.reportValidity();
        return;
      }

      clearSubmitError();
      triggerAReviewsSubmit();
    });

    return wrapper;
  }

  /* -----------------------------------------------------------
     Dialog open/close — bypasses the theme's <dialog-component> showDialog/
     closeDialog because those apply `document.body { position: fixed;
     top: -scrollY }` as scroll lock. On iOS Safari, that combination with
     a <dialog> in the top layer + on-screen keyboard transitions causes the
     top-layer rectangle to be recomputed mid-frame on every keyboard show/
     hide and on every focus switch between inputs — producing a visible
     blank-frame flicker (the dialog briefly appears empty and re-paints).

     Cross-references: WebKit #153224 (input-in-fixed scroll-to-top regression),
     Bootstrap #22491, Headless UI #3412 / #1900, MUI #28718 — all describe the
     same symptom and all stopped using body-fixed scroll lock.

     Senior fix: use the theme's own `html[scroll-lock] { overflow: hidden }`
     rule (assets/base.css:35) — the same rule used for <details scroll-lock>
     drawers and confirmed to NOT trigger the iOS visual-viewport bug. Drives
     the dialog directly with showModal() / close(), with our own backdrop-click
     and Escape handlers, and runs modalSlideOutTop on close.
     ----------------------------------------------------------- */

  function openDialog(wrapper) {
    const dialog = wrapper.querySelector('dialog');
    if (!dialog || dialog.open) return;

    document.documentElement.setAttribute('scroll-lock', '');
    dialog.showModal();

    dialog.addEventListener('click', handleDialogBackdropClick);
    dialog.addEventListener('cancel', handleDialogCancel);
    wrapper.dispatchEvent(new CustomEvent('dialog:open'));
  }

  function closeDialog(wrapper) {
    const dialog = wrapper.querySelector('dialog');
    if (!dialog || !dialog.open) return;

    dialog.removeEventListener('click', handleDialogBackdropClick);
    dialog.removeEventListener('cancel', handleDialogCancel);

    /* Restart any in-flight enter animation so the closing animation runs
       cleanly (mirrors the trick the theme's DialogComponent uses). */
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      dialog.removeEventListener('animationend', finish);
      dialog.classList.remove('dialog-closing');
      dialog.close();
      /* Only release the scroll-lock if no OTHER aRev dialog is still open
         (e.g. write dialog closes immediately followed by success dialog open). */
      if (!document.querySelector('dialog-component dialog[open]')) {
        document.documentElement.removeAttribute('scroll-lock');
      }
      wrapper.dispatchEvent(new CustomEvent('dialog:close'));
    };
    dialog.addEventListener('animationend', finish);
    /* Fallback for prefers-reduced-motion or any case animationend doesn't fire. */
    setTimeout(finish, 500);
  }

  /* Back-compat shims — original names used elsewhere in this file. */
  const openWriteDialog = openDialog;
  const closeWriteDialog = closeDialog;

  function handleDialogBackdropClick(event) {
    /* When the user clicks the visible content (descendant of <dialog>), the
       event target is the descendant. When they click the backdrop area, the
       target is the <dialog> itself (the backdrop is a pseudo-element of the
       dialog and bubbles to it). Use that to detect backdrop clicks. */
    if (event.target !== event.currentTarget) return;
    const wrapper = event.currentTarget.closest('dialog-component');
    if (wrapper) closeWriteDialog(wrapper);
  }

  function handleDialogCancel(event) {
    /* Two unrelated events both named `cancel` bubble up to the dialog:
         - <dialog> fires `cancel` on Escape (target = the dialog)
         - <input type="file"> fires `cancel` when the user dismisses the
           OS file picker without choosing a file (Chrome 113+, target =
           the input)
       Without filtering, dismissing the file picker here would close the
       dialog. Only act on cancel events whose target is the dialog itself. */
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    const wrapper = event.currentTarget.closest('dialog-component');
    if (wrapper) closeWriteDialog(wrapper);
  }

  /* Submit via direct fetch with FormData. We used to click AReviews's submit
     button and let their inline-eval'd handler do the POST, but that handler:
       - throws "Cannot set properties of null" because it tries to mutate UI
         elements we hid/restructured in the form
       - silently bails when files are selected (their pre-flight image-upload
         step depends on DOM elements that aren't where it expects)
     Direct POST gives us full control over the submission lifecycle and works
     identically with or without images (multipart/form-data carries both).

     Three fields the AReviews handler injects at submit time aren't present
     in the form HTML — we replicate that injection here:
       - product_id  ← #az_reviews[data-product_id]
       - customer_country ← Shopify.locale or Shopify.country (lowercased ISO)
       - customer_id ← window.customer_id (set by the AReviews boot script) */
  function triggerAReviewsSubmit() {
    const formEl = document.querySelector('#enter_reviews');
    if (!formEl) return;

    const formData = new FormData(formEl);

    const azEl = document.querySelector('#az_reviews');
    if (azEl?.dataset?.product_id) {
      formData.set('product_id', azEl.dataset.product_id);
    }
    try {
      const localeOrCountry =
        (window.Shopify && (window.Shopify.locale || window.Shopify.country)) || '';
      if (localeOrCountry) {
        formData.set('customer_country', String(localeOrCountry).split('-')[0].toLowerCase());
      }
    } catch (e) { /* ignore */ }
    /* Only set customer_id when it's a meaningful value. AReviews's boot script
       defaults `window.customer_id = ''` for guests; explicitly empty is fine.
       Skip the set for null/undefined so the backend treats it as guest. */
    const cid = window.customer_id;
    if (cid != null) formData.set('customer_id', String(cid));

    document.dispatchEvent(new CustomEvent('arev:submission-start'));

    fetch(SUBMIT_URL, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
    })
      .then((res) => {
        /* Shopify can return a 200 OK with an HTML login page when the session
           expired — `res.ok` alone would falsely report success. Confirm the
           content-type isn't HTML before treating it as a real success. */
        const contentType = (res.headers.get('content-type') || '').toLowerCase();
        const looksLikeLoginRedirect = contentType.includes('text/html');
        if (res.ok && !looksLikeLoginRedirect) {
          document.dispatchEvent(new CustomEvent('arev:submission-success'));
        } else {
          document.dispatchEvent(
            new CustomEvent('arev:submission-error', {
              detail: { status: res.status, reason: looksLikeLoginRedirect ? 'session' : 'http' },
            })
          );
        }
      })
      .catch(() => {
        document.dispatchEvent(
          new CustomEvent('arev:submission-error', { detail: { status: 0, reason: 'network' } })
        );
      });
  }

  /* -----------------------------------------------------------
     Submission lifecycle — driven entirely from triggerAReviewsSubmit's fetch
     (above). It dispatches `arev:submission-start` synchronously before send,
     `arev:submission-success` when the response is 2xx with the expected
     content-type, and `arev:submission-error` on any failure. We DO NOT patch
     XMLHttpRequest here: doing so would (a) be dead code now that we own the
     submission via fetch and (b) risk double-firing events if any other AReviews
     codepath also POSTs to the same endpoint. It would also wrap every XHR on
     the page (analytics, chat widgets, etc.) just to filter on URL — wasteful.
     ----------------------------------------------------------- */

  /* -----------------------------------------------------------
     Success dialog — Linda Casa #modal-review-success replica.
     ----------------------------------------------------------- */
  function ensureSuccessDialog() {
    let wrapper = document.getElementById(SUCCESS_DIALOG_ID);
    if (wrapper) return wrapper;

    wrapper = document.createElement('dialog-component');
    wrapper.id = SUCCESS_DIALOG_ID;
    wrapper.className = 'aRev-success-dialog';
    wrapper.innerHTML =
      '<dialog ref="dialog" class="aRev-success-dialog__content dialog-modal color-scheme-1" scroll-lock aria-labelledby="aRev-success-title">' +
      '<div class="aRev-success-dialog__inner">' +
      `<button type="button" class="button-unstyled aRev-success-dialog__close" aria-label="Fechar">${CLOSE_X_SVG}</button>` +
      `<span class="aRev-success-dialog__check">${CHECK_CIRCLE_SVG}</span>` +
      '<h3 id="aRev-success-title" class="aRev-success-dialog__title">Sucesso!</h3>' +
      '<p class="aRev-success-dialog__subtitle">Sua avaliação foi enviada.</p>' +
      '<button type="button" class="aRev-success-dialog__btn">Fechar</button>' +
      '</div>' +
      '</dialog>';
    document.body.appendChild(wrapper);

    const close = () => closeDialog(wrapper);
    wrapper.querySelector('.aRev-success-dialog__close').addEventListener('click', close);
    wrapper.querySelector('.aRev-success-dialog__btn').addEventListener('click', close);
    return wrapper;
  }

  /* -----------------------------------------------------------
     Media viewer — Instagram-Stories-style lightbox for review media.
     Built on the same <dialog-component> + dialog-modal primitives as the
     other dialogs so it inherits iOS keyboard / scroll-lock / animation
     fixes.

     Interaction model:
       - Top progress bars (one per item, filled = visited+current)
       - Header overlay: avatar (initials), name, stars, comment
       - Stage: image / video centered with object-fit: contain
       - Mobile gestures (Pointer Events — covers touch + mouse + pen):
           * Tap left third → previous
           * Tap right third → next
           * Swipe horizontal → previous / next
           * Swipe down → close
       - Desktop: arrow buttons visible on hover, ArrowLeft/Right keys nav
       - Escape closes (native <dialog> cancel)
     ----------------------------------------------------------- */
  const NAV_PREV_SVG =
    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const NAV_NEXT_SVG =
    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function ensureMediaViewer() {
    let wrapper = document.getElementById(MEDIA_VIEWER_ID);
    if (wrapper) return wrapper;

    wrapper = document.createElement('dialog-component');
    wrapper.id = MEDIA_VIEWER_ID;
    wrapper.className = 'aRev-media-viewer';
    wrapper.innerHTML =
      '<dialog ref="dialog" class="aRev-media-viewer__content dialog-modal color-scheme-1" scroll-lock aria-label="Visualizador de mídia">' +
      '<div class="aRev-media-viewer__inner">' +
      '<div class="aRev-media-viewer__topbar">' +
      '<div class="aRev-media-viewer__progress" role="presentation"></div>' +
      `<button type="button" class="button-unstyled aRev-media-viewer__close" aria-label="Fechar visualizador">${CLOSE_X_SVG}</button>` +
      '</div>' +
      '<div class="aRev-media-viewer__header">' +
      '<div class="aRev-media-viewer__avatar" aria-hidden="true"></div>' +
      '<div class="aRev-media-viewer__meta">' +
      '<div class="aRev-media-viewer__name"></div>' +
      '<div class="aRev-media-viewer__rating"></div>' +
      '<p class="aRev-media-viewer__comment"></p>' +
      '</div>' +
      '</div>' +
      '<div class="aRev-media-viewer__stage"></div>' +
      `<button type="button" class="button-unstyled aRev-media-viewer__nav aRev-media-viewer__nav--prev" aria-label="Mídia anterior">${NAV_PREV_SVG}</button>` +
      `<button type="button" class="button-unstyled aRev-media-viewer__nav aRev-media-viewer__nav--next" aria-label="Próxima mídia">${NAV_NEXT_SVG}</button>` +
      '</div>' +
      '</dialog>';
    document.body.appendChild(wrapper);

    wrapper.querySelector('.aRev-media-viewer__close').addEventListener('click', () => closeDialog(wrapper));
    wrapper.querySelector('.aRev-media-viewer__nav--prev').addEventListener('click', () => navigateMediaViewer(wrapper, -1));
    wrapper.querySelector('.aRev-media-viewer__nav--next').addEventListener('click', () => navigateMediaViewer(wrapper, +1));
    wrapper.querySelector('dialog').addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); navigateMediaViewer(wrapper, -1); }
      else if (event.key === 'ArrowRight') { event.preventDefault(); navigateMediaViewer(wrapper, +1); }
    });

    /* Release retained references when the viewer closes. _aRevItems holds an
       array of cloned media nodes (image + video DOM); without this cleanup,
       reopening the viewer for a different review accumulated detached
       <video> elements in memory across the session. The stage HTML is
       cleared independently in renderMediaViewerSlide on each open, so we
       only need to drop the JS-level refs here. */
    wrapper.addEventListener('dialog:close', () => {
      const stage = wrapper.querySelector('.aRev-media-viewer__stage');
      if (stage) {
        /* Pause any playing video before discarding so it doesn't keep
           audio running during the close animation. */
        stage.querySelectorAll('video').forEach((v) => {
          try { v.pause(); v.removeAttribute('src'); v.load(); } catch (_) {}
        });
        stage.innerHTML = '';
      }
      wrapper._aRevItems = null;
      wrapper._aRevIdx = 0;
    });

    setupMediaViewerGestures(wrapper);
    return wrapper;
  }

  /* Touch / mouse gestures via Pointer Events. We compute distance + duration
     between pointerdown and pointerup to distinguish a tap from a swipe, then
     check the X position of the tap (for tap-to-nav zones) or the dominant
     swipe axis (horizontal → nav, vertical down → close). All in one handler,
     consistent across touch + mouse + pen. */
  function setupMediaViewerGestures(wrapper) {
    const stage = wrapper.querySelector('.aRev-media-viewer__stage');
    if (!stage) return;
    let pointer = null;
    const TAP_DIST = 8;       // px — anything below = a tap
    const TAP_TIME = 300;     // ms — anything above = held, treat as gesture
    const SWIPE_THRESHOLD = 50;

    stage.addEventListener('pointerdown', (e) => {
      /* Skip if the pointer landed on an interactive child (video controls,
         video element itself for play/pause). Buttons live OUTSIDE the stage
         so they don't trigger this anyway. */
      if (e.target.closest('video, button, a, input')) return;
      pointer = { x: e.clientX, y: e.clientY, t: Date.now(), id: e.pointerId };
    });

    stage.addEventListener('pointerup', (e) => {
      if (!pointer || pointer.id !== e.pointerId) return;
      const dx = e.clientX - pointer.x;
      const dy = e.clientY - pointer.y;
      const dist = Math.hypot(dx, dy);
      const dt = Date.now() - pointer.t;
      pointer = null;

      if (dist < TAP_DIST && dt < TAP_TIME) {
        const rect = stage.getBoundingClientRect();
        const tapX = e.clientX - rect.left;
        if (tapX < rect.width * 0.33) navigateMediaViewer(wrapper, -1);
        else if (tapX > rect.width * 0.67) navigateMediaViewer(wrapper, +1);
        return;
      }
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);
      if (absY > absX && dy > 80) {
        closeDialog(wrapper);
      } else if (absX > SWIPE_THRESHOLD && absX > absY) {
        navigateMediaViewer(wrapper, dx < 0 ? +1 : -1);
      }
    });

    stage.addEventListener('pointercancel', () => { pointer = null; });
  }

  function openMediaViewer(items, startIdx) {
    const wrapper = ensureMediaViewer();
    wrapper._aRevItems = items;
    wrapper._aRevIdx = Math.max(0, Math.min(startIdx, items.length - 1));
    renderMediaViewerProgress(wrapper);
    renderMediaViewerHeader(wrapper);
    renderMediaViewerSlide(wrapper);
    openDialog(wrapper);
  }

  function navigateMediaViewer(wrapper, delta) {
    const items = wrapper._aRevItems || [];
    if (items.length <= 1) return;
    const len = items.length;
    wrapper._aRevIdx = ((wrapper._aRevIdx + delta) % len + len) % len;
    renderMediaViewerProgress(wrapper);
    renderMediaViewerSlide(wrapper);
  }

  function renderMediaViewerProgress(wrapper) {
    const items = wrapper._aRevItems || [];
    const idx = wrapper._aRevIdx;
    const progress = wrapper.querySelector('.aRev-media-viewer__progress');
    if (!progress) return;
    progress.innerHTML = '';
    items.forEach((_, i) => {
      const seg = document.createElement('span');
      seg.className = 'aRev-media-viewer__progress-segment';
      if (i <= idx) seg.classList.add('is-visited');
      if (i === idx) seg.classList.add('is-current');
      progress.appendChild(seg);
    });
  }

  function renderMediaViewerHeader(wrapper) {
    const items = wrapper._aRevItems || [];
    const reviewer = items[wrapper._aRevIdx]?.reviewer;
    if (!reviewer) return;

    const avatar = wrapper.querySelector('.aRev-media-viewer__avatar');
    const name = wrapper.querySelector('.aRev-media-viewer__name');
    const rating = wrapper.querySelector('.aRev-media-viewer__rating');
    const comment = wrapper.querySelector('.aRev-media-viewer__comment');

    avatar.textContent = avatarInitials(reviewer.name);
    avatar.style.backgroundColor = avatarColor(reviewer.name);

    name.textContent = reviewer.name || 'Anônimo';

    rating.innerHTML = '';
    for (let i = 0; i < 5; i++) {
      rating.insertAdjacentHTML('beforeend', starSvg(13, i < (reviewer.starsCount || 0)));
    }

    if (reviewer.comment) {
      comment.textContent = reviewer.comment;
      comment.hidden = false;
    } else {
      comment.textContent = '';
      comment.hidden = true;
    }
  }

  function renderMediaViewerSlide(wrapper) {
    const items = wrapper._aRevItems || [];
    const idx = wrapper._aRevIdx;
    const item = items[idx];
    if (!item) return;

    const stage = wrapper.querySelector('.aRev-media-viewer__stage');
    stage.innerHTML = '';
    if (item.isVideo) {
      const video = document.createElement('video');
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      video.src = item.fullUrl;
      stage.appendChild(video);
    } else {
      const img = document.createElement('img');
      img.src = item.fullUrl;
      img.alt = '';
      img.decoding = 'async';
      img.draggable = false;
      stage.appendChild(img);
    }

    const multi = items.length > 1;
    wrapper.querySelector('.aRev-media-viewer__nav--prev').hidden = !multi;
    wrapper.querySelector('.aRev-media-viewer__nav--next').hidden = !multi;
  }

  function avatarInitials(name) {
    if (!name) return '?';
    return name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join('') || '?';
  }

  function avatarColor(name) {
    if (!name) return '#999999';
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = (hash << 5) - hash + name.charCodeAt(i);
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 55%, 48%)`;
  }

  function resetAReviewsForm() {
    const form = document.querySelector('#enter_reviews');
    if (!form) return;
    /* DO NOT call form.reset() — it restores every input to its HTML default,
       which wipes the AReviews-injected hidden inputs (product_id, csrf,
       customer_id, etc.) that were populated by the app's boot script. The
       next submission would then go out with empty/wrong values. Reset only
       the visible fields we control. */
    const nameInput = form.querySelector('#comment_name');
    if (nameInput) nameInput.value = '';
    const emailInput = form.querySelector('#comment_email');
    if (emailInput) emailInput.value = '';
    const textarea = form.querySelector('#review_comment');
    if (textarea) textarea.value = '';
    /* Clear the file input ourselves — the only field types form.reset() would
       touch that we actually want cleared. */
    const fileInput = form.querySelector('#areviewsinputfile');
    if (fileInput) {
      try { fileInput.value = ''; } catch (e) { /* IE quirks — ignore */ }
    }
    /* Reset our star widget (visual + aria + roving tabindex). */
    const starButtons = form.querySelectorAll('.aRev-stars .aRev-star');
    starButtons.forEach((b, idx) => {
      b.classList.remove('aRev-star-filled');
      b.setAttribute('aria-checked', 'false');
      b.tabIndex = idx === 0 ? 0 : -1;
    });
    /* Mirror clear to AReviews's original spans (kept hidden in the form). */
    form.querySelectorAll('#areviews_form_stars span').forEach((s) => {
      s.classList.remove('fa-star');
      s.classList.add('fa-star-o');
      s.setAttribute('aria-checked', 'false');
    });
    /* Clear the hidden rating input so the next submission starts from 0. */
    const ratingInput = form.querySelector('#comment_rating_ra');
    if (ratingInput) ratingInput.value = '';
    /* Clear any uploaded thumbnail previews so the next review starts fresh. */
    const thumbs = form.querySelector('.areviews-thumb-output');
    if (thumbs) thumbs.innerHTML = '';
    const ourPreview = form.querySelector('.aRev-field__preview');
    if (ourPreview) ourPreview.innerHTML = '';
    /* Bring the slot icons back (we hide them when a file is selected). */
    const slots = form.querySelector('.aRev-field__images');
    if (slots) slots.style.display = '';
    /* Reset our char counter UI. */
    const counter = form.querySelector('.aRev-field__counter');
    if (textarea && counter) {
      const max = parseInt(textarea.getAttribute('maxlength'), 10) || 250;
      counter.textContent = `${max} caracteres restantes`;
    }
  }

  function handleSubmissionSuccess() {
    const writeWrapper = document.getElementById(WRITE_DIALOG_ID);
    const showSuccess = () => {
      resetAReviewsForm();
      openDialog(ensureSuccessDialog());
    };

    if (!writeWrapper) {
      /* No write dialog open — show success immediately. */
      showSuccess();
      return;
    }

    const proxy = writeWrapper.querySelector('.aRev-write-dialog__submit');
    if (proxy) setSubmitProxyLoading(proxy, false);

    /* Wait for the close animation to fully play before opening the success
       dialog. Previously this was a magic `setTimeout(..., 320)` matching the
       CSS animation duration — fragile against any future tweak to that
       duration in areviews-custom.css. closeDialog() dispatches `dialog:close`
       on the wrapper exactly when its animationend listener (or the 500ms
       fallback) fires, so we hook that instead. Single-use listener +
       belt-and-suspenders timeout to guarantee progress in pathological cases
       (e.g. wrapper detached mid-close, animation interrupted). */
    let advanced = false;
    const advance = () => {
      if (advanced) return;
      advanced = true;
      writeWrapper.removeEventListener('dialog:close', advance);
      showSuccess();
    };
    writeWrapper.addEventListener('dialog:close', advance, { once: true });
    /* Fallback caps total wait at ~600ms even if the close handler never
       dispatches (closeDialog itself has a 500ms internal fallback, +100ms
       buffer to let it run first). */
    setTimeout(advance, 600);

    closeDialog(writeWrapper);
  }

  function handleSubmissionStart() {
    /* triggerAReviewsSubmit dispatches this AFTER our own validation passed
       and immediately before the fetch fires. Activate the spinner so the
       user gets feedback while the network request is in flight. */
    const proxy = document.querySelector('.aRev-write-dialog__submit');
    if (proxy) setSubmitProxyLoading(proxy, true);
  }

  function handleSubmissionError(event) {
    /* POST failed (4xx/5xx, expired session, or network) — re-enable the submit
       button so the user can retry, and surface a message via aria-live so
       screen-reader users + sighted users both get feedback. */
    const proxy = document.querySelector('.aRev-write-dialog__submit');
    if (proxy) setSubmitProxyLoading(proxy, false);
    const reason = event?.detail?.reason;
    let message = 'Não foi possível enviar a avaliação. Tente novamente.';
    if (reason === 'session') message = 'Sua sessão expirou. Atualize a página e tente novamente.';
    else if (reason === 'network') message = 'Sem conexão com o servidor. Verifique sua internet e tente novamente.';
    showSubmitError(message);
  }

  function showSubmitError(message) {
    const wrapper = document.getElementById(WRITE_DIALOG_ID);
    if (!wrapper) return;
    let region = wrapper.querySelector('.aRev-write-dialog__error');
    if (!region) {
      region = document.createElement('div');
      region.className = 'aRev-write-dialog__error';
      region.setAttribute('role', 'alert');
      region.setAttribute('aria-live', 'assertive');
      const footer = wrapper.querySelector('.aRev-write-dialog__footer');
      if (footer) footer.parentElement.insertBefore(region, footer);
    }
    region.textContent = message;
    region.hidden = false;
  }

  function clearSubmitError() {
    const wrapper = document.getElementById(WRITE_DIALOG_ID);
    const region = wrapper?.querySelector('.aRev-write-dialog__error');
    if (region) { region.textContent = ''; region.hidden = true; }
  }

  /* Build a clean ARIA-compliant star-rating widget. AReviews's native starrr
     widget renders via FontAwesome ::before glyphs scoped to `#az_reviews` —
     outside that scope it can't render and its click handlers can't be relied
     on (they're bound by XHR-eval'd scripts that may target the original
     parent). Rather than fight that broken widget, we build our own and write
     directly to the source of truth: the hidden input `#comment_rating_ra`,
     which is what AReviews's submit handler POSTs. We also mirror the state
     to the original starrr container's spans (aria-checked + class toggle)
     so any other piece of AReviews JS that introspects them sees consistent
     state. No hover preview — only click sets the rating, matching the
     reference (Linda Casa) pattern. Keyboard: ArrowLeft/Right + Space/Enter
     follow the W3C radiogroup pattern. */
  function buildStarRatingWidget(form) {
    const widget = document.createElement('div');
    widget.className = 'aRev-stars';
    widget.setAttribute('role', 'radiogroup');
    widget.setAttribute('aria-labelledby', 'aRev-stars-label');

    const STAR_INNER = `<svg viewBox="0 0 14 13" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="${STAR_PATH}"/></svg>`;
    const buttons = [];
    for (let i = 1; i <= 5; i++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'aRev-star';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', 'false');
      btn.setAttribute('aria-label', `${i} ${i === 1 ? 'estrela' : 'estrelas'}`);
      btn.dataset.value = String(i);
      btn.tabIndex = i === 1 ? 0 : -1;
      btn.innerHTML = STAR_INNER;
      buttons.push(btn);
      widget.appendChild(btn);
    }

    /* Apply a rating across all parallel state: our buttons (visual + aria),
       the source-of-truth hidden input, and AReviews's original spans.
       The hidden input + original spans are re-queried inside the function
       (NOT captured at widget-build time) because AReviews can recreate the
       starrr container after we built — stale references would silently break
       the cross-state mirroring. */
    function applyRating(value) {
      if (!Number.isInteger(value) || value < 0 || value > 5) return;
      buttons.forEach((b, idx) => {
        const v = idx + 1;
        b.classList.toggle('aRev-star-filled', v <= value);
        b.setAttribute('aria-checked', String(v === value));
        /* Roving tabindex: the focused / selected radio is the only one in
           the tab order. Falls back to the first if nothing selected. */
        b.tabIndex = (value > 0 ? v === value : v === 1) ? 0 : -1;
      });
      const ratingInput = form.querySelector('#comment_rating_ra');
      if (ratingInput) ratingInput.value = String(value);
      form.querySelectorAll('#areviews_form_stars span').forEach((s, idx) => {
        const v = idx + 1;
        s.setAttribute('aria-checked', String(v === value));
        s.classList.toggle('fa-star', v <= value);
        s.classList.toggle('fa-star-o', v > value);
      });
    }

    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const v = parseInt(btn.dataset.value, 10);
        applyRating(v);
      });
      btn.addEventListener('keydown', (event) => {
        const idx = buttons.indexOf(btn);
        let nextIdx = idx;
        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            nextIdx = Math.min(idx + 1, buttons.length - 1);
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            nextIdx = Math.max(idx - 1, 0);
            break;
          case 'Home':
            nextIdx = 0;
            break;
          case 'End':
            nextIdx = buttons.length - 1;
            break;
          default:
            return;
        }
        event.preventDefault();
        applyRating(nextIdx + 1);
        buttons[nextIdx].focus();
      });
    });

    /* Initial sync from any pre-existing hidden-input value (shouldn't happen
       in normal first-open, but covers re-opens after a partial reset). */
    const initialInput = form.querySelector('#comment_rating_ra');
    const initial = initialInput ? parseInt(initialInput.value, 10) || 0 : 0;
    applyRating(initial);

    return widget;
  }

  /* Wire the file input's `change` event to render preview thumbnails using
     FileReader. Each preview shows the image cropped to a small square plus
     a remove (×) button that drops that file from the selection. We rebuild
     the FileList on remove because file inputs don't expose a remove API. */
  function wireFilePreview(fileInput, previewRow, slotsRow) {
    fileInput.addEventListener('change', () => render());

    function render() {
      previewRow.innerHTML = '';
      const files = Array.from(fileInput.files || []);
      if (slotsRow) slotsRow.style.display = files.length ? 'none' : '';
      files.forEach((file, idx) => {
        if (!/^image\/|^video\//.test(file.type)) return;
        const item = document.createElement('div');
        item.className = 'aRev-preview-item';

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'aRev-preview-item__remove';
        remove.setAttribute('aria-label', 'Remover imagem');
        remove.innerHTML =
          '<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
          '<path d="M11 3L3 11M3 3l8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
          '</svg>';
        remove.addEventListener('click', () => {
          /* DataTransfer.items.add isn't implemented in Safari iOS < 14.1 —
             feature-detect and fall back to clearing the input entirely (the
             user re-picks remaining files). Better than failing silently. */
          const supportsDT = typeof DataTransfer !== 'undefined' &&
            (() => { try { return !!new DataTransfer().items.add; } catch (e) { return false; } })();
          if (supportsDT) {
            const dt = new DataTransfer();
            Array.from(fileInput.files || []).forEach((f, i) => { if (i !== idx) dt.items.add(f); });
            fileInput.files = dt.files;
          } else {
            try { fileInput.value = ''; } catch (e) { /* ignore */ }
          }
          fileInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
        item.appendChild(remove);

        if (file.type.startsWith('video/')) {
          const video = document.createElement('video');
          video.src = URL.createObjectURL(file);
          video.muted = true;
          video.playsInline = true;
          item.appendChild(video);
        } else {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = document.createElement('img');
            img.src = e.target.result;
            img.alt = '';
            item.insertBefore(img, item.firstChild);
          };
          reader.readAsDataURL(file);
        }

        previewRow.appendChild(item);
      });
    }
  }

  function flashStarsInvalid(form) {
    const widget = form.querySelector('.aRev-stars');
    if (!widget) return;
    widget.classList.remove('aRev-stars--invalid');
    /* Reflow so re-adding the class restarts the shake animation. */
    void widget.offsetWidth;
    widget.classList.add('aRev-stars--invalid');
    const firstStar = widget.querySelector('.aRev-star');
    if (firstStar) firstStar.focus();
    setTimeout(() => widget.classList.remove('aRev-stars--invalid'), 1200);
  }

  function setSubmitProxyLoading(proxy, loading) {
    if (loading) {
      if (!proxy.dataset.aRevOriginalText) {
        proxy.dataset.aRevOriginalText = proxy.textContent || 'Enviar avaliação';
      }
      proxy.dataset.aRevBusy = '1';
      proxy.disabled = true;
      proxy.classList.add('is-loading');
      proxy.innerHTML =
        '<span class="aRev-spinner" aria-hidden="true"></span>' +
        '<span class="aRev-write-dialog__submit-text">Enviando...</span>';
    } else {
      proxy.dataset.aRevBusy = '';
      proxy.disabled = false;
      proxy.classList.remove('is-loading');
      proxy.textContent = proxy.dataset.aRevOriginalText || 'Enviar avaliação';
    }
  }

  function moveFormIntoDialog(wrapper) {
    const form = document.querySelector('#enter_reviews');
    if (!form) return null;
    if (!form.dataset.aRevOriginRemembered) {
      form.dataset.aRevOriginRemembered = '1';
      form._aRevOriginParent = form.parentElement;
      form._aRevOriginNext = form.nextSibling;
    }
    const body = wrapper.querySelector('.aRev-write-dialog__body');
    if (body && form.parentElement !== body) body.appendChild(form);
    /* Reset every inline style — AReviews's submit handler animates the form
       out by setting `height: 0; opacity: 0; transition: height 700ms`, and
       on the second open we'd otherwise reopen with an invisible form. Wipe
       cssText completely and re-set just `display: block`. */
    form.style.cssText = 'display: block;';
    decorateFormLikeLindaCasa(form);
    return form;
  }

  function returnFormToOrigin() {
    const form = document.querySelector('#enter_reviews');
    if (!form || !form._aRevOriginParent) return;
    const parent = form._aRevOriginParent;
    const next = form._aRevOriginNext;
    if (next && next.parentElement === parent) {
      parent.insertBefore(form, next);
    } else if (parent && parent.isConnected) {
      parent.appendChild(form);
    }
    form.style.display = 'none';
  }

  function triggerWriteReview() {
    const wrapper = ensureWriteDialog();
    const form = moveFormIntoDialog(wrapper);
    if (!form) return;
    openWriteDialog(wrapper);
  }

  /* -----------------------------------------------------------
     Reorganize the AReviews form to match the Linda Casa layout:
       1. QUANTIDADE DE ESTRELAS *
       2. NOME *
       3. E-MAIL
       4. AVALIAÇÃO *
       5. Imagem do produto (opcional)
       6. Rules
     Hide the app's internal submit button (we use our footer proxy).
     ----------------------------------------------------------- */
  function decorateFormLikeLindaCasa(form) {
    if (form.dataset.aRevDecorated === '1') return;
    form.dataset.aRevDecorated = '1';

    const $ = (sel, root) => (root || form).querySelector(sel);

    // -- Build wrapping fields with our own labels --
    function fieldFor(input, labelText, opts) {
      const wrapper = document.createElement('div');
      wrapper.className = 'aRev-field';
      if (opts?.required) wrapper.classList.add('aRev-field--required');
      const label = document.createElement('label');
      label.className = 'aRev-field__label';
      label.htmlFor = input.id || '';
      label.innerHTML = labelText + (opts?.required ? ' <span class="aRev-field__star">*</span>' : '');
      wrapper.appendChild(label);
      return wrapper;
    }

    // 1. STARS — replace AReviews's broken-out-of-context starrr widget with
    //    our own ARIA radiogroup. The hidden #comment_rating_ra input is the
    //    source of truth that AReviews's submit reads, so we just keep that
    //    in sync. The original #areviews_form_stars container stays in the
    //    form (hidden) so any internal AReviews JS reference still resolves.
    const starsContainer = $('#areviews_form_stars');
    let starsField = null;
    if (starsContainer) {
      starsField = document.createElement('div');
      starsField.className = 'aRev-field aRev-field--required';
      starsField.innerHTML = '<label class="aRev-field__label" id="aRev-stars-label">QUANTIDADE DE ESTRELAS <span class="aRev-field__star">*</span></label>';
      const widget = buildStarRatingWidget(form);
      starsField.appendChild(widget);
      /* Keep the original starrr container in the field so it's still owned by
         the form (for AReviews internal references), but visually hidden. */
      starsContainer.style.display = 'none';
      starsField.appendChild(starsContainer);
    }

    // 2. NAME
    const nameInput = $('#comment_name');
    let nameField = null;
    if (nameInput) {
      nameInput.placeholder = 'Ex.: Maria';
      nameField = fieldFor(nameInput, 'NOME', { required: true });
      nameField.appendChild(nameInput);
    }

    // 3. EMAIL
    const emailInput = $('#comment_email');
    let emailField = null;
    if (emailInput) {
      emailInput.placeholder = 'Ex.: maria@gmail.com';
      emailField = fieldFor(emailInput, 'E-MAIL', { required: false });
      emailField.appendChild(emailInput);
    }

    // 4. REVIEW TEXT + char counter
    const textarea = $('#review_comment');
    let textField = null;
    if (textarea) {
      textarea.placeholder = 'Escreva o que você achou deste produto...';
      textarea.setAttribute('maxlength', '250');
      textarea.setAttribute('rows', '4');
      textField = fieldFor(textarea, 'AVALIAÇÃO', { required: true });
      textField.appendChild(textarea);
      const max = parseInt(textarea.getAttribute('maxlength'), 10) || 250;
      const counter = document.createElement('div');
      counter.className = 'aRev-field__counter';
      const update = () => {
        counter.textContent = `${max - textarea.value.length} caracteres restantes`;
      };
      textarea.addEventListener('input', update);
      update();
      textField.appendChild(counter);
    }

    // 5. IMAGE UPLOAD
    const fileInput = $('#areviewsinputfile');
    const fileLabel = form.querySelector('label[for="areviewsinputfile"]');
    const thumbsOut = form.querySelector('.areviews-thumb-output');
    let imageField = null;
    if (fileInput) {
      imageField = document.createElement('div');
      imageField.className = 'aRev-field';
      imageField.innerHTML =
        '<label class="aRev-field__label">Imagem do produto <span class="aRev-field__optional">(opcional)</span></label>' +
        '<div class="aRev-field__images"></div>';
      const slotsRow = imageField.querySelector('.aRev-field__images');
      const slotIcon =
        '<svg viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<rect x="0.5" y="0.5" width="95" height="95" rx="8" fill="#F5F5F5" stroke="#999999" stroke-dasharray="3 3"></rect>' +
        '<path d="M6.86 4.22L1.21 11.84a.5.5 0 00.4.79h18.91a.5.5 0 00.45-.71L16.03 1.05a.5.5 0 00-.87-.07L11.28 6.82a.5.5 0 01-.76.09L7.6 4.16a.5.5 0 00-.74.06z" fill="#999999" stroke="#999999" transform="translate(40,42)"></path>' +
        '<circle cx="38" cy="40" r="2.6" fill="#999999"></circle>' +
        '<rect x="30" y="30" width="37" height="33" rx="4" fill="none" stroke="#999999" stroke-dasharray="3 3"></rect>' +
        '</svg>';
      // 3 visual slots (only the first one is wired to the real input)
      const wireSlot = document.createElement('label');
      wireSlot.className = 'aRev-field__image-slot';
      wireSlot.htmlFor = 'areviewsinputfile';
      wireSlot.innerHTML = slotIcon;
      slotsRow.appendChild(wireSlot);
      for (let i = 0; i < 2; i++) {
        const placeholder = document.createElement('div');
        placeholder.className = 'aRev-field__image-slot is-disabled';
        placeholder.innerHTML = slotIcon;
        slotsRow.appendChild(placeholder);
      }
      // Move the real input + thumb output into the field (kept hidden by CSS)
      imageField.appendChild(fileInput);
      if (fileLabel) fileLabel.remove();
      if (thumbsOut) imageField.appendChild(thumbsOut);
      /* Our own preview row, populated via FileReader on input change. We
         can't trust AReviews's `.areviews-thumb-output` because their image
         processing JS is fragile against our DOM restructure (sometimes
         doesn't fire, sometimes throws). FileReader on the input gives us
         deterministic preview generation that doesn't depend on the app. */
      const previewRow = document.createElement('div');
      previewRow.className = 'aRev-field__preview';
      imageField.appendChild(previewRow);
      wireFilePreview(fileInput, previewRow, slotsRow);
    }

    // 6. RULES
    const rules = document.createElement('div');
    rules.className = 'aRev-field__rules';
    rules.innerHTML =
      '• Sua avaliação está sujeita à aprovação por nossa equipe<br>' +
      '• Seus dados não serão divulgados<br>' +
      '• Não publicaremos termos ofensivos ou de baixo calão.';

    /* CRITICAL: do NOT clear the form. The AReviews app loads this form via XHR
       and runs inline <script>s that bind handlers + hold direct JS references
       to many of its DOM nodes (rating displays, validation divs, the submit
       button, hidden inputs). Destroying any of them makes those scripts blow
       up with `Cannot read properties of null` when the user clicks submit
       (e.g. setRating tries to set innerHTML on a node we deleted).

       The inputs we extracted (#comment_name, #comment_email, #review_comment,
       #areviews_form_stars, #areviewsinputfile, .areviews-thumb-output) have
       already been moved into our wrappers above — `appendChild` MOVES nodes,
       so they're still in the DOM, just relocated. Now we wrap our visible
       fields in a single container and INSERT it at the start of the form,
       leaving every other original child (validation, errors, submit button,
       hidden inputs) untouched in their original positions. CSS hides them
       offscreen so they're invisible but still queryable + clickable. */
    const visual = document.createElement('div');
    visual.className = 'aRev-form-visual';
    if (starsField) visual.appendChild(starsField);
    if (nameField) visual.appendChild(nameField);
    if (emailField) visual.appendChild(emailField);
    if (textField) visual.appendChild(textField);
    if (imageField) visual.appendChild(imageField);
    visual.appendChild(rules);
    form.insertBefore(visual, form.firstChild);
  }

  /* -----------------------------------------------------------
     Title row: 3 product-anchor buttons (rating / chat / link)

     Iterates ALL matching containers so multi-instance layouts (e.g. one
     AreviewsTitleRating app block in the Header group of _product-details
     plus another inside blocks/_pdp-mobile-header-slot.liquid for mobile
     reorder) all receive the rebuilt markup. Each container carries its
     own idempotency signature on dataset.aRevSignature so signature
     checks remain correct per-instance.

     Multi-instance quirk of the AReviews app: when the page has 2+
     AreviewsTitleRating app blocks, their Liquid template only populates
     a `.areviews_main_header_container` inside ONE of them (the others
     get only the stray `.areviews_header_stars` empty div). We seed the
     missing container ourselves below so our forEach can rebuild both,
     giving desktop (details instance) AND mobile (slot instance) a pill.
     ----------------------------------------------------------- */
  /* Click handler shared by both seed and rebuild — scrolls to the
     reviews section's outer wrapper so the merchant's heading lands
     at the top of viewport (sticky-header offset via scroll-margin-top
     CSS on the wrapper). */
  function pillScrollHandler(e) {
    e.preventDefault();
    const az = document.getElementById('az_reviews');
    const target = az && (az.closest('.shopify-section') || az);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* Build the .aRev-anchor button DOM once. The same element is used
     for both the loading state (with `aRev-anchor--loading` class +
     placeholder text) and the rebuilt state (with real text content).
     Critical: SAME structure → swapping is a textContent update, NOT
     a DOM rebuild. Eliminates the geometric resize that occurred
     between an `::before` skeleton (92×35 with leading) and the rebuilt
     button (84×35, no leading). */
  function buildAnchorEl() {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'aRev-anchor aRev-anchor--rating aRev-anchor--loading';
    btn.setAttribute('aria-label', 'Carregando avaliações');
    btn.innerHTML =
      starSvg(15, true) +
      '<span class="aRev-anchor__value">\u00a0</span>' +
      '<span class="aRev-anchor__count">\u00a0</span>';
    btn.addEventListener('click', pillScrollHandler);
    return btn;
  }

    function unhideBadgeAncestors(container) {
    if (!container) return;
    let n = container.parentElement;
    let guard = 0;
    while (n && n !== document.body && guard++ < 12) {
      const isAppBlock = n.matches('[data-block-handle="AreviewsTitleRating"], [id*="areviews_title_rating"]');
      if (getComputedStyle(n).display === 'none') {
        n.style.setProperty('display', 'block', 'important');
      }
      if (isAppBlock) break;
      n = n.parentElement;
    }
  }

  function watchBadgeAncestors() {
    if (window.__aRevBadgeWatcher) return;
    window.__aRevBadgeWatcher = true;
    const obs = new MutationObserver(() => {
      document.querySelectorAll('.areviews_main_header_container.aRev-seeded')
        .forEach(unhideBadgeAncestors);
    });
    document.querySelectorAll('.areviews_header_stars').forEach((el) => {
      obs.observe(el, { attributes: true, attributeFilter: ['style', 'class'] });
    });
  }

  function applyTitleBadge() {
    /* OWNERSHIP INVARIANT + ZERO-CLS SEED:

       1. Tag every seeded container with `.aRev-seeded`. CSS hides
          any `.areviews_main_header_container` lacking the tag, so
          AReviews's `injectProductRating` orphans never paint.
       2. Build the REAL `.aRev-anchor` button structure at seed time
          with placeholder content. Loading state styling lives on the
          `.aRev-anchor--loading` class. When data arrives we just
          update the inner spans' textContent — same DOM, same metrics.
          No `::before`, no inline-block leading, no width estimate.
          The container's natural width tracks the button's width in
          both states; rebuilt-state width may shift slightly with
          longer content (e.g. "Novo!" → "4.6"), but eliminates the
          jarring 8px+6px shrink the user observed.

       Seeding handles app blocks AReviews didn't populate (singleton
       behavior — only one instance per page gets AReviews's container). */
    document.querySelectorAll(AREV_TITLE_BLOCK_SEL).forEach((appBlock) => {
      const existing = appBlock.querySelector('.areviews_main_header_container.aRev-seeded');
      if (existing) return;
      // If the server-rendered Liquid already populated a container in this
      // block, promote it to seeded (preserves whatever AReviews painted)
      // rather than appending a second empty one. Without this branch, the
      // populated container stays unseeded → hidden by the .aRev-seeded
      // CSS guard, and our seeded sibling renders the empty placeholder.
      const populated = appBlock.querySelector('.areviews_main_header_container');
      if (populated) {
        populated.classList.add('aRev-seeded');
        return;
      }
      const container = document.createElement('div');
      container.className = 'areviews_main_header_container aRev-seeded';
      container.appendChild(buildAnchorEl());
      appBlock.appendChild(container);
    });

    const containers = document.querySelectorAll('.areviews_main_header_container.aRev-seeded');
    if (!containers.length) { dlog('applyTitleBadge: no seeded containers'); return; }

    // Defer data update until AReviews's server data is hydrated, BUT
    // only up to the fallback deadline (10s). On timeout we render the
    // empty-state ("Novo!") rather than leaving placeholders forever.
    const dataReady = getReviewsCount() !== null;
    if (!dataReady && performance.now() < aRevDataDeadline) {
      dlog('applyTitleBadge: #totalReviews not yet present, waiting (deadline in ' +
        Math.round(aRevDataDeadline - performance.now()) + 'ms)');
      return;
    }
    if (!dataReady) dwarn('applyTitleBadge: deadline reached without #totalReviews — falling through to "Novo!"');

    const rating = getAverageRating();
    const count = getReviewsCount() || 0;
    const signature = `${rating === null ? 'null' : rating}|${count}`;
    const ratingText = rating === null
      ? 'Novo!'
      : (Number.isInteger(rating) ? String(rating) : rating.toFixed(1));
    const countText = `(${count})`;

    containers.forEach((container) => {
      if (container.dataset.aRevSignature === signature) return;
      container.dataset.aRevSignature = signature;

      let anchor = container.querySelector('.aRev-anchor');
      if (!anchor) {
        anchor = buildAnchorEl();
        container.innerHTML = '';
        container.appendChild(anchor);
      }
      anchor.classList.remove('aRev-anchor--loading');
      anchor.removeAttribute('aria-label');
      const valueEl = anchor.querySelector('.aRev-anchor__value');
      const countEl = anchor.querySelector('.aRev-anchor__count');
      if (valueEl) valueEl.textContent = ratingText;
      if (countEl) countEl.textContent = countText;

      // Update the star fill to match data state — empty star for "Novo!",
      // filled yellow for actual ratings. Replaces the SVG in place rather
      // than re-creating the button.
      const star = anchor.querySelector('.aRev-star path');
      if (star) star.setAttribute('fill', rating === null ? '#cfcfcf' : '#FFCC00');

      container.classList.add('aRev-rebuilt');
      container.removeAttribute('href');
    });
  }

  /* -----------------------------------------------------------
     Empty state (0 reviews) — full-width cream card
     ----------------------------------------------------------- */
  function applyEmptyState() {
    const az = document.querySelector(reviewsRootSel);
    if (!az) { dlog('applyEmptyState: no #az_reviews — abort'); return; }

    // The form lives in its own <dialog> now, so we no longer need to
    // suppress empty-state recalculation while the user is editing.

    const count = getReviewsCount();
    if (count === null) { dlog('applyEmptyState: count=null — waiting'); return; }

    if (count === 0) {
      // Remove any skeleton holder we seeded before knowing this is empty
      const loadingHolder = az.querySelector('.aRev-holder--loading');
      if (loadingHolder) loadingHolder.remove();
      az.classList.add('aRev-empty');
      if (!az.querySelector('.aRev-emptyState')) {
        const empty = document.createElement('div');
        empty.className = 'aRev-emptyState';
        empty.innerHTML =
          '<div class="aRev-emptyState__card">' +
          '<div class="aRev-emptyState__message">Produto novo, sem avaliações ainda.<br>' +
          '<strong>Seja a 1ª pessoa a avaliar!</strong></div>' +
          '<button type="button" class="aRev-btn aRev-btn--tertiary aRev-emptyState__button">Avaliar produto</button>' +
          '</div>';
        empty.querySelector('button').addEventListener('click', triggerWriteReview);
        az.insertBefore(empty, az.firstElementChild);
      }
    } else {
      az.classList.remove('aRev-empty');
      const existing = az.querySelector('.aRev-emptyState');
      if (existing) existing.remove();
    }
  }

  /* -----------------------------------------------------------
     With-reviews state: build our own .aRev-holder (title +
     2-col grid: sticky summary | masonry 3-col cards).
     ----------------------------------------------------------- */
  /* Persistent buffer of every source card we've seen across page loads.
     AReviews uses traditional pagination (clicking "Next Page" REPLACES the
     #reviewsBody contents with the next page's reviews — we lose the previous
     page from the DOM). For Linda-Casa-style "Carregar mais" UX (append, never
     replace), we ingest every page into this accumulator on each render pass,
     deduped by a fingerprint, and render from the accumulator instead of from
     the live #reviewsBody. The accumulator survives page swaps. */
  let sourceAccumulator = [];
  let sourceAccumulatorFps = new Set();
  let sourceAccumulatorProductId = null;
  /* Set when the user clicks "Carregar mais"; flips back to false once new
     cards land in the accumulator. Used to (a) drive the loading dots on the
     load-more button and (b) tag the freshly-ingested cards with `isNew` so
     the render pass adds the entry animation only on those. */
  let isLoadingMore = false;

  /* Cheap djb2-style string hash — sufficient for fingerprint dedupe (we just
     need different inputs to produce different outputs with high probability). */
  function strHash(s) {
    let h = 5381;
    for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i);
    return (h >>> 0).toString(36);
  }

  /* Locate the comment element associated with a source card. AReviews
     renders comment markup either as a child of the card itself OR as a
     sibling that follows immediately after — depends on theme/version.
     Single source of truth for both fingerprintSourceCard (uses comment
     text in the fingerprint hash) and ingestCurrentPageSources (clones
     the comment node into the accumulator). Without sharing, divergence
     between the two would silently break the dedup invariant. */
  function findCommentFor(card) {
    const direct = card.querySelector('.comment') || card.querySelector('.comment_container');
    if (direct) return direct;
    let next = card.nextElementSibling;
    while (next) {
      if (next.matches?.('.callout, .areviews_slide_image_details')) return null;
      if (next.matches?.('.comment, .comment_container')) return next;
      next = next.nextElementSibling;
    }
    return null;
  }

  function fingerprintSourceCard(card) {
    const name = (card.querySelector('.areviews_user_name')?.textContent || '').trim();
    const time = (card.querySelector('.comment_time, .areviews_comment_time')?.textContent || '').trim();
    const stars = extractStarsCount(card);
    /* Include a hash of the comment text so two reviews from the same name
       with the same rating on the same day don't collide (popular products). */
    const commentEl = findCommentFor(card);
    const commentText = (commentEl?.textContent || '').trim();
    return `${name}|${stars}|${time}|${strHash(commentText)}`;
  }

  /* Reset the accumulator if we navigated to a different product. Without
     this the buffer would grow unbounded across SPA-style navigations,
     leaking cloned DOM nodes per product visited.

     Gate on `pid` truthy: AReviews can briefly render `#az_reviews` before
     populating `data-product_id`. Treating null as a distinct product would
     wipe a freshly-ingested accumulator the moment the real id arrives. */
  function maybeResetAccumulator() {
    const az = document.querySelector(reviewsRootSel);
    const pid = az?.dataset?.product_id || null;
    if (pid && pid !== sourceAccumulatorProductId) {
      sourceAccumulator = [];
      sourceAccumulatorFps = new Set();
      sourceAccumulatorProductId = pid;
    }
  }

  function ingestCurrentPageSources(reviewsBody) {
    const sources = arevResolveAll('cards', reviewsBody);
    let added = 0;
    sources.forEach((card) => {
      const fp = fingerprintSourceCard(card);
      if (sourceAccumulatorFps.has(fp)) return;
      sourceAccumulatorFps.add(fp);

      const commentEl = findCommentFor(card);
      sourceAccumulator.push({
        fp,
        card: card.cloneNode(true),
        comment: commentEl ? commentEl.cloneNode(true) : null,
        /* Mark as new only if this ingest happened during a load-more cycle.
           The first-page ingest (initial render) doesn't get the animation
           because that whole section appears as one chunk on page load. */
        isNew: isLoadingMore,
      });
      added++;
    });
    /* Once new cards have arrived, clear the loading flag (instead of relying
       on the safety timeout). The next render will use isNew on those cards
       (and only those) for the entry animation, and the load-more button will
       be re-rendered without the loading state. */
    if (added > 0 && isLoadingMore) isLoadingMore = false;
  }

  /* Build a single card DOM element from one accumulator entry. Extracted
     so both the full-rebuild and incremental-append paths share identical
     output. `isFreshlyAdded` controls the entry animation. `masonryRef`
     is threaded to `buildCardMediaRow` so image-load callbacks avoid
     `closest()` walks. */
  function buildCardEl(entry, isFreshlyAdded, staggerIndex, masonryRef) {
    const source = entry.card;
    const name = (source.querySelector('.areviews_user_name')?.textContent || '').trim();
    const starsCount = extractStarsCount(source);
    const commentEl = entry.comment || source.querySelector('.comment, .comment_container');
    const commentText = (commentEl?.textContent || '').trim();

    const holderEl = document.createElement('div');
    holderEl.className = 'aRev-holder-card';
    if (isFreshlyAdded) {
      holderEl.classList.add('aRev-holder-card--enter');
      holderEl.style.animationDelay = `${staggerIndex * 50}ms`;
    }

    const card = document.createElement('div');
    card.className = 'aRev-card';

    const nameRow = document.createElement('div');
    nameRow.className = 'aRev-card__name';
    nameRow.innerHTML = `<span class="aRev-card__name-text">${name}</span>`;

    const starsRow = document.createElement('div');
    starsRow.className = 'aRev-card__rating';
    starsRow.setAttribute('aria-label', `${starsCount} de 5 estrelas`);
    for (let i = 0; i < 5; i++) {
      starsRow.insertAdjacentHTML('beforeend', starSvg(15, i < starsCount));
    }

    card.appendChild(nameRow);
    card.appendChild(starsRow);

    if (commentText) {
      const textEl = document.createElement('div');
      textEl.className = 'aRev-card__text';
      textEl.innerHTML = `"<span class="aRev-card__text-inner">${commentText}</span>"`;
      card.appendChild(textEl);
    }

    const mediaRow = buildCardMediaRow(source, { name, starsCount, comment: commentText }, masonryRef);
    if (mediaRow) card.appendChild(mediaRow);

    holderEl.appendChild(card);
    return holderEl;
  }

  /* Build/refresh the load-more control. Toggles visibility based on
     `count > sourceAccumulator.length`. Reuses existing element when
     present so the click-handler closure isn't duplicated. */
  function ensureLoadMore(content, count) {
    let loadMore = content.querySelector('.aRev-loadmore');
    const needsLoadMore = count > sourceAccumulator.length;

    if (!needsLoadMore) {
      if (loadMore) loadMore.remove();
      return;
    }

    if (loadMore) {
      // Already present — make sure the loading state is cleared if the
      // previous click resolved.
      if (!isLoadingMore) {
        loadMore.classList.remove('is-loading');
        const link = loadMore.querySelector('.aRev-loadmore__link');
        if (link) link.textContent = 'Carregar mais avaliações';
      }
      // Move to end of content (defensive — ensures it stays after masonry).
      content.appendChild(loadMore);
      return;
    }

    loadMore = document.createElement('div');
    loadMore.className = 'aRev-loadmore';
    loadMore.innerHTML = '<span class="aRev-link-alike aRev-loadmore__link">Carregar mais avaliações</span>';
    const link = loadMore.querySelector('span');
    link.addEventListener('click', (e) => {
      e.preventDefault();
      if (isLoadingMore) return;
      isLoadingMore = true;
      loadMore.classList.add('is-loading');
      link.innerHTML = '<span class="aRev-loadmore__dots" aria-label="Carregando">' +
        '<span></span><span></span><span></span></span>';
      const ok = triggerAReviewsLoadMore();
      setTimeout(() => {
        if (isLoadingMore) {
          isLoadingMore = false;
          loadMore.classList.remove('is-loading');
          link.textContent = 'Carregar mais avaliações';
        }
      }, 15000);
      if (!ok) {
        isLoadingMore = false;
        loadMore.classList.remove('is-loading');
        link.textContent = 'Carregar mais avaliações';
      }
    });
    content.appendChild(loadMore);
  }

  /* Build a skeleton holder for the reviews section that mirrors the
     final rebuilt structure (.aRev-holder > .aRev-grid > [.aRev-average +
     .aRev-content > .aRev-masonry > N cards]). Same-structure approach:
     when data arrives, applyReviewsLayout removes the skeleton holder
     and builds the real one — zero structural surprise.

     Renders 6 placeholder cards with varied heights to mimic the
     masonry feel. Each card has name + stars + 2-3 text bars. The
     average panel shows rating placeholder + 5 grade bars + button.

     ====================================================================
     INTENTIONAL NO-OP since the FOUC fix.
     ====================================================================
     This used to paint a structural skeleton (`.aRev-holder--loading` with
     summary panel + 6 masonry cards) AND tag #az_reviews with
     `.aRev-rebuilt`. That tag turned off the critical-CSS guard
     (snippets/areviews-critical-css.liquid), so users saw a sequence of
     three visual states:
       1. Generic gray 480px placeholder (from critical CSS)
       2. Structural skeleton (from this function)
       3. Real content (from applyReviewsLayout)
     User reported "two skeletons" — exactly the 1→2 transition.

     Fix: drop the structural skeleton entirely. The critical CSS reserves
     480px with `visibility: hidden` on children — that's the only loading
     state users see. When real data arrives, applyReviewsLayout paints
     the final content AND tags `.aRev-rebuilt`, swapping the placeholder
     for content in a single frame.

     The function is kept as a callable no-op rather than removed because
     applyReviewsLayout has multiple call-sites threading through it for
     "data not yet ready" gates. Inlining a no-op here keeps the call-site
     intent visible ("we wanted to show a skeleton, but we don't") and
     centralizes future tweaks (e.g. if we want to revive a skeleton path
     conditionally).
     ==================================================================== */
  function seedReviewsHolderSkeleton() {
    /* See block comment above for why this is a deliberate no-op. */
    if (AREV_DEBUG) {
      const az = document.querySelector(reviewsRootSel);
      dlog('seedReviewsHolderSkeleton: no-op (FOUC fix — critical CSS handles loading state). az=' + !!az);
    }
  }

  function applyReviewsLayout() {
    const az = document.querySelector(reviewsRootSel);
    if (!az) { dlog('applyReviewsLayout: no #az_reviews — abort'); return; }

    const count = getReviewsCount();
    if (count === null) {
      // Data not yet arrived — paint skeleton if we haven't already.
      dlog('applyReviewsLayout: count=null (#totalReviews not populated) — seeding/keeping skeleton');
      seedReviewsHolderSkeleton();
      return;
    }
    if (count === 0) { dlog('applyReviewsLayout: count=0 — empty state path'); return; }

    const reviewsBody = arevResolveEl('bodyEl');
    if (!reviewsBody) {
      // Data says count > 0 but body not yet populated — keep the skeleton.
      dlog('applyReviewsLayout: count=' + count + ' but #reviewsBody missing — keeping skeleton');
      seedReviewsHolderSkeleton();
      return;
    }

    /* Wipe the accumulator if we landed on a different product (SPA nav). */
    maybeResetAccumulator();
    const beforeLen = sourceAccumulator.length;
    ingestCurrentPageSources(reviewsBody);
    if (sourceAccumulator.length === 0) {
      dlog('applyReviewsLayout: count=' + count + ' but no .callout.areviews_slide_image_details ingested (sources in body=' +
        reviewsBody.querySelectorAll('.callout.areviews_slide_image_details').length + ') — keeping skeleton');
      return;
    }
    if (sourceAccumulator.length !== beforeLen) {
      dlog('applyReviewsLayout: ingested ' + (sourceAccumulator.length - beforeLen) + ' new sources (total=' + sourceAccumulator.length + ')');
    }

    const rating = getAverageRating();
    const dist = getRatingDistribution();
    const ratingFormatted = rating !== null ? rating.toFixed(1) : '0.0';

    /* Two-tier signature for incremental render:
       - summarySignature drives FULL rebuild (rating/count/distribution
         changes are rare — only on new review submission)
       - accumulator length is tracked separately on `.aRev-masonry` via
         data-aRev-rendered-count; on summary-stable renders we APPEND
         only the new entries instead of destroying the holder.
       Old approach destroyed and rebuilt all 80 cards on every load-more
       click — measured 200-500ms freeze on mid-tier mobile. This path
       does O(newCards) work per click. */
    const summarySignature = `${rating}|${count}|${[5,4,3,2,1].map((n) => dist[n] || 0).join(',')}`;
    let holder = az.querySelector('.aRev-holder');
    const masonry = holder?.querySelector('.aRev-masonry');

    if (holder && masonry && holder.dataset.aRevSummarySignature === summarySignature) {
      // INCREMENTAL PATH — only the accumulator may have grown.
      const lastRendered = parseInt(masonry.dataset.aRevRenderedCount || '0', 10);
      if (lastRendered === sourceAccumulator.length) return;
      let newCounter = 0;
      for (let i = lastRendered; i < sourceAccumulator.length; i++) {
        const entry = sourceAccumulator[i];
        const isFresh = !!entry.isNew;
        const cardEl = buildCardEl(entry, isFresh, newCounter, masonry);
        if (isFresh) { entry.isNew = false; newCounter++; }
        masonry.appendChild(cardEl);
      }
      masonry.dataset.aRevRenderedCount = String(sourceAccumulator.length);
      const content = holder.querySelector('.aRev-content');
      if (content) ensureLoadMore(content, count);
      scheduleMasonrySizing(masonry);
      return;
    }

    // FULL REBUILD — summary changed (rating/count/distribution).
    if (holder) holder.remove();

    holder = document.createElement('div');
    holder.className = 'aRev-holder';
    holder.dataset.aRevSummarySignature = summarySignature;

    const grid = document.createElement('div');
    grid.className = 'aRev-grid';
    holder.appendChild(grid);

    const summary = document.createElement('div');
    summary.className = 'aRev-average';
    summary.innerHTML =
      '<div class="aRev-average__total">' +
      starSvg(39, true) +
      `<span class="aRev-average__number">${ratingFormatted}</span>` +
      `<p class="aRev-average__helper">${count} ${count === 1 ? 'avaliação' : 'avaliações'}</p>` +
      '</div>' +
      '<div class="aRev-average__grades">' +
      [5, 4, 3, 2, 1]
        .map(
          (n) =>
            '<div class="aRev-grade">' +
            `<div class="aRev-grade__bar"><div class="aRev-grade__bar-fill" style="width:${dist[n] || 0}%"></div></div>` +
            `<span class="aRev-grade__index">${n}</span>` +
            starSvg(15, true) +
            '</div>'
        )
        .join('') +
      '</div>' +
      '<button type="button" class="aRev-btn aRev-btn--tertiary aRev-average__button">Avaliar produto</button>';
    summary.querySelector('.aRev-average__button').addEventListener('click', triggerWriteReview);
    grid.appendChild(summary);

    const content = document.createElement('div');
    content.className = 'aRev-content';

    const newMasonry = document.createElement('div');
    newMasonry.className = 'aRev-masonry';
    content.appendChild(newMasonry);

    let newCardCounter = 0;
    sourceAccumulator.forEach((entry) => {
      const isFresh = !!entry.isNew;
      const cardEl = buildCardEl(entry, isFresh, newCardCounter, newMasonry);
      if (isFresh) { entry.isNew = false; newCardCounter++; }
      newMasonry.appendChild(cardEl);
    });
    newMasonry.dataset.aRevRenderedCount = String(sourceAccumulator.length);

    ensureLoadMore(content, count);

    grid.appendChild(content);

    az.classList.add('aRev-rebuilt');
    az.insertBefore(holder, az.firstElementChild);

    scheduleMasonrySizing(newMasonry);
  }

  /* -----------------------------------------------------------
     Build the media-thumbnail row for a review card. Returns null when the
     source has no media. Each thumbnail opens the in-page media viewer
     dialog (NOT a new tab — the viewer uses the same dialog primitives as
     every other modal in this file: <dialog-component> + dialog-modal +
     modalSlideInTop + html[scroll-lock]). After each <img> finishes loading
     we re-run masonry sizing because card height grows once the image
     reports its intrinsic dimensions.

     Lazy-load gotcha: AReviews's <img src> starts as a placeholder
     (white_imge.png) and only becomes the real URL after the image enters
     the viewport. The <a href> is the real URL from the start, so we prefer
     that (and any data-src/data-original attribute) over the visible src.
     ----------------------------------------------------------- */
  /* Trigger AReviews's pagination "next page" — verified via Playwright that
     the actual control is a `<span role="link" aria-label="Next Page" data-url>`
     inside `.pagination` (NOT a <button> or <a>). AReviews's delegated click
     handler fetches data-url and replaces #reviewsBody with the new page's
     reviews; our MutationObserver re-runs applyReviewsLayout, which ingests
     the new cards into sourceAccumulator (deduped) and re-renders the masonry
     with all accumulated cards — that's how the page swap becomes "load more". */
  function triggerAReviewsLoadMore() {
    const az = document.querySelector('#az_reviews');
    if (!az) return false;
    /* Primary: aria-label = "Next Page" (most reliable across versions). */
    let trigger = az.querySelector('.pagination span[role="link"][aria-label="Next Page"]');
    if (!trigger) {
      /* Find a clickable span whose data-url page > current active page. */
      const active = az.querySelector('.pagination li.active span, .pagination li.active a');
      const currentPage = active ? parseInt((active.textContent || '').trim(), 10) || 1 : 1;
      const candidates = az.querySelectorAll('.pagination span[role="link"][data-url], .pagination a[data-url], .pagination a[href]');
      for (const el of candidates) {
        const url = el.getAttribute('data-url') || el.getAttribute('href') || '';
        const pageMatch = url.match(/[?&]page=(\d+)/);
        if (pageMatch && parseInt(pageMatch[1], 10) > currentPage) { trigger = el; break; }
      }
    }
    /* Legacy load-more button selectors (for older AReviews versions). */
    if (!trigger) {
      trigger =
        az.querySelector('.areviews_load_more') ||
        az.querySelector('.areviews_load_more_wrapper button') ||
        az.querySelector('.areviews_load_more_container button') ||
        az.querySelector('a[rel="next"]') ||
        az.querySelector('.pagination-next a');
    }
    if (trigger) { trigger.click(); return true; }
    if (typeof window.loadMoreReviews === 'function') { window.loadMoreReviews(); return true; }
    if (typeof window.areviews_load_more === 'function') { window.areviews_load_more(); return true; }
    /* Nothing matched. Selector drift in a future AReviews version is the
       most likely cause. Surface once so the author notices in QA. */
    if (!triggerAReviewsLoadMore.warned) {
      triggerAReviewsLoadMore.warned = true;
      console.warn('[areviews-custom] No load-more / pagination control found in #az_reviews. UI built but selectors may have drifted.');
    }
    return false;
  }

  /* Extract the rating (number of filled stars) from a source review card.
     AReviews has rendered stars in at least three different ways across
     versions, so we probe each in order and use the first that yields a
     non-zero result:
       1. Material Icons (current):       <i class="material-icons">star</i>
                                          <i class="material-icons">star_border</i>
       2. FontAwesome 4 (legacy):         <i class="fa fa-star"></i>      = filled
                                          <i class="fa fa-star-o"></i>    = empty
       3. FontAwesome 5 (intermediate):   <i class="fas fa-star"></i>     = filled
                                          <i class="far fa-star"></i>     = empty
     Final fallback: hidden input `#get_stars<id>` carries the raw value (0–5).
     If everything fails we return 0 — far less misleading than always showing
     5 stars (the previous `|| 5` default was the actual bug). */
  function extractStarsCount(source) {
    const container =
      source.querySelector('.main_container_review_stars') ||
      source.querySelector('.main_review_stars') ||
      source.querySelector('.child_review_stars');

    if (container) {
      // 1. Material Icons — count <i> whose textContent is exactly "star"
      const materialIcons = container.querySelectorAll('.material-icons');
      if (materialIcons.length) {
        let n = 0;
        materialIcons.forEach((i) => { if ((i.textContent || '').trim() === 'star') n++; });
        if (n > 0) return n;
      }

      // 2 + 3. FontAwesome (any version): elements with `fa-star` class but
      // NOT empty markers (`fa-star-o` for FA4, or `far` solid-style for FA5)
      const icons = Array.from(container.querySelectorAll('i, span'));
      let n = 0;
      icons.forEach((el) => {
        const cls = (typeof el.className === 'string' ? el.className : el.className?.baseVal || '');
        if (!/\bfa-star\b/.test(cls)) return;
        const isEmpty = /\bfa-star-o\b/.test(cls) || /\bfar\b/.test(cls);
        if (!isEmpty) n++;
      });
      if (n > 0) return n;
    }

    // Fallback: hidden input the AReviews dashboard exposes — sometimes mirrored
    // on the storefront card (id pattern: get_stars<reviewId>).
    const hidden = source.querySelector('input[id^="get_stars"]');
    if (hidden) {
      const v = parseInt(hidden.value, 10);
      if (Number.isFinite(v) && v >= 0 && v <= 5) return v;
    }

    /* All probes failed. AReviews probably changed its star markup. Warn ONCE
       so QA notices in console — silently rendering 0 stars on every review
       (degraded but not broken) would otherwise mask the regression. */
    if (!extractStarsCount.warned) {
      extractStarsCount.warned = true;
      console.warn('[areviews-custom] Could not extract star rating from review card. AReviews markup may have changed.', source);
    }
    return 0;
  }

  function buildCardMediaRow(source, reviewer, masonryRef) {
    const anchors = source.querySelectorAll('.image_container a, .images_theme2 a');
    if (!anchors.length) return null;

    const items = [];
    anchors.forEach((orig) => {
      const isVideo = !!orig.dataset?.videoUrl;
      const fullUrl = orig.getAttribute('href') || orig.dataset?.videoUrl || '';
      const innerImg = orig.querySelector('img');
      const lazySrc =
        innerImg?.getAttribute('data-src') ||
        innerImg?.getAttribute('data-original') ||
        innerImg?.getAttribute('data-lazy-src') ||
        innerImg?.getAttribute('data-srcset') ||
        '';
      const visibleSrc = innerImg?.getAttribute('src') || '';
      const isPlaceholder = /white_imge|placeholder|blank|spacer/i.test(visibleSrc);
      const thumbUrl = lazySrc || (isPlaceholder ? fullUrl : visibleSrc) || fullUrl;
      const finalUrl = fullUrl || lazySrc || visibleSrc;
      if (!thumbUrl && !finalUrl) return;
      items.push({
        thumbUrl: thumbUrl || finalUrl,
        fullUrl: finalUrl,
        isVideo,
        reviewer: reviewer || null,
      });
    });
    if (!items.length) return null;

    const row = document.createElement('div');
    row.className = 'aRev-card__media';

    items.forEach((item, idx) => {
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'aRev-card__media-item';
      if (item.isVideo) trigger.classList.add('aRev-card__media-item--video');
      trigger.setAttribute('aria-label', item.isVideo ? 'Ver vídeo da avaliação' : 'Ver imagem da avaliação');

      const img = document.createElement('img');
      img.src = item.thumbUrl;
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.addEventListener('load', () => {
        // Cached masonry reference passed from applyReviewsLayout — avoids
        // a `closest()` walk per image (potentially 80+ per page).
        scheduleMasonrySizing(masonryRef || trigger.closest('.aRev-masonry'));
      }, { once: true });
      trigger.appendChild(img);

      if (item.isVideo) {
        trigger.insertAdjacentHTML(
          'beforeend',
          '<span class="aRev-card__media-play" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M8 5v14l11-7z"/></svg>' +
          '</span>'
        );
      }

      trigger.addEventListener('click', () => openMediaViewer(items, idx));
      row.appendChild(trigger);
    });

    return row.childElementCount ? row : null;
  }

  /* -----------------------------------------------------------
     CSS Grid masonry sizing.
     With grid-auto-rows: 1px and gap: 16px, each card needs:
       grid-row-end: span ceil((cardHeight + gap) / (1 + gap))
     so the row track total covers the card's own height.

     Two-phase to avoid layout thrashing: collect ALL heights first (read
     phase), then write ALL grid-row-end values in a second pass. The naive
     read-write-read-write loop forces synchronous layout on every iteration.
     ----------------------------------------------------------- */
  function sizeMasonryCards(masonry) {
    if (!masonry) return;
    const rowHeight = 1;
    const rowGap = 16;
    const cards = Array.from(masonry.querySelectorAll('.aRev-holder-card'));
    const spans = new Array(cards.length);
    /* Read phase — measure all cards back-to-back (single layout pass). */
    for (let i = 0; i < cards.length; i++) {
      const inner = cards[i].querySelector('.aRev-card');
      if (!inner) continue;
      const h = inner.getBoundingClientRect().height;
      spans[i] = Math.ceil((h + rowGap) / (rowHeight + rowGap));
    }
    /* Write phase — assign all grid-row-end at once (no interleaved reads). */
    for (let i = 0; i < cards.length; i++) {
      if (spans[i]) cards[i].style.gridRowEnd = `span ${spans[i]}`;
    }
  }

  /* Coalesce many image-load callbacks into a single masonry resize per
     animation frame. With 30 images loading in parallel the naive approach
     ran 30 layouts in rapid succession; this collapses them to one. */
  let pendingMasonrySizing = null;
  function scheduleMasonrySizing(masonry) {
    if (!masonry) return;
    if (pendingMasonrySizing) return;
    pendingMasonrySizing = requestAnimationFrame(() => {
      pendingMasonrySizing = null;
      sizeMasonryCards(masonry);
    });
  }

  /* Resize handler — recalc spans when viewport width changes.

     Earlier iteration aborted this listener on `pagehide` to clean up.
     That was wrong: `pagehide` fires when the page enters BFCache, and
     restored pages don't re-execute scripts. Aborting on pagehide meant
     back-navigated visitors saw broken masonry on resize/rotation.

     The listener is intentionally kept live for the page's lifetime.
     The actual leak risk (one resize listener per page nav) is bounded
     by the number of distinct page loads — every script-eval creates
     exactly one listener; full-page nav releases the entire JS context.
     SPA-style nav within Horizon doesn't re-execute this script, so the
     listener is created once and survives correctly. No leak. */
  let resizeTimer = null;
  window.addEventListener('resize', () => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      const masonry = document.querySelector('.aRev-masonry');
      if (masonry) sizeMasonryCards(masonry);
    }, 150);
  });

  let runCounter = 0;
  function run() {
    runCounter++;
    if (AREV_DEBUG) debugSnapshot('run#' + runCounter + ' start');
    const steps = [
      ['promoteCustomStylesheet', promoteCustomStylesheet],
      ['applyMobileDedup', applyMobileDedup],
      ['applyTitleBadge', applyTitleBadge],
      ['applyEmptyState', applyEmptyState],
      ['applyReviewsLayout', applyReviewsLayout],
      ['unhideAll', () => {
        document.querySelectorAll('.areviews_main_header_container.aRev-seeded')
          .forEach(unhideBadgeAncestors);
        watchBadgeAncestors();
      }],
    ];
    for (const [nome, fn] of steps) {
      try { fn(); }
      catch (e) { console.error('[areviews-custom] erro em ' + nome, e); }
    }
  }

  function init() {
    dlog('init: starting (readyState=' + document.readyState + ')');
    /* Submission lifecycle events are dispatched directly by triggerAReviewsSubmit
       (fetch-based). No XHR patching anywhere — see the long comment above
       ensureSuccessDialog for why. */
    document.addEventListener('arev:submission-start', handleSubmissionStart);
    document.addEventListener('arev:submission-success', handleSubmissionSuccess);
    document.addEventListener('arev:submission-error', handleSubmissionError);

    /* Pre-create our three <dialog>s up-front so the first showModal() call
       on user interaction is paint-instant. Without this, the dialog node
       is created lazily inside the click handler — between insertion into
       <body> and the next style recalc, the browser briefly paints the
       <dialog> with user-agent default styles (black border, no background)
       before our `.dialog-modal` / `.color-scheme-1` rules apply. The
       resulting flash was visible on every first "Avaliar produto" click.

       ensureXxxDialog() is idempotent (returns the existing node on second
       call), so the lazy-creation paths in triggerWriteReview() etc. still
       work as no-ops once init has primed the cache. */
    try {
      ensureWriteDialog();
      ensureSuccessDialog();
      ensureMediaViewer();
    } catch (e) {
      console.error('[areviews-custom] falha ao pré-criar dialogs (seguindo mesmo assim)', e);
    }

    run();

    /* -----------------------------------------------------------
       Targeted observers — observe AReviews's data-injection points
       (XHR-driven), NOT the entire #az_reviews subtree.

       Why: subtree:true on #az_reviews caught every mutation we
       caused ourselves (holder rebuild, masonry sizing, lazy-load
       image loads...). Each one re-scheduled run(). On a product
       with 40 reviews, page load triggered 40+ run() invocations
       — each ~5-20ms on mobile. Up to 800ms of wasted main-thread
       work, kept stable only by signature idempotency. One bug in
       any rebuild path away from infinite loop.

       Split:
       (a) `#az_reviews` ROOT-CHILDREN ONLY (no subtree) — fires on
           XHR replacing innerHTML or our holder swap. Rare events.
       (b) `#totalReviews` text mutation — rating data arrival, the
           gate `applyTitleBadge` waits on. Fires once per nav.
       (c) `[data-block-handle="AreviewsTitleRating"]` direct
           children — catches AReviews's `injectProductRating()`
           inserting orphan containers (same race the ownership
           invariant defeats, but observing lets us re-run dedup
           immediately instead of relying on signature idempotency).

       All share one rAF-coalesced run scheduler — at most one
       run() per animation frame regardless of how many fire.
       ----------------------------------------------------------- */
    let scheduled = false;
    const scheduleRun = (origin) => {
      if (origin && AREV_DEBUG) dlog('scheduleRun: triggered by ' + origin);
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        run();
      });
    };
    /* Wrap MutationObserver callback so we can label which observer fired */
    const obsCallback = (label) => () => scheduleRun(label);

    const azReviews = document.querySelector(reviewsRootSel);
    if (!azReviews) dwarn('init: #az_reviews not in DOM at init time');
    if (azReviews) {
      // (a) shallow childList on `#az_reviews`: XHR replaces innerHTML,
      // our holder swap, etc.
      new MutationObserver(obsCallback('observer:#az_reviews children')).observe(azReviews, { childList: true });

      // (a.2) shallow childList on `#reviewsBody`: AReviews's load-more
      // pagination replaces `#reviewsBody.innerHTML` with the next page.
      // The mutation happens INSIDE #az_reviews's subtree but on a node
      // deeper than #az_reviews's immediate children — so observer (a)
      // alone misses it. Without this, clicking "Carregar mais" never
      // schedules a run() and the loading state hangs until the 15s
      // safety timeout. We also re-attach this observer when AReviews
      // recreates #reviewsBody (full XHR replace) via a delegating
      // observer on #az_reviews subtree.
      const attachReviewsBodyObserver = () => {
        const rb = document.getElementById('reviewsBody');
        if (!rb || rb.__aRevObserved) return;
        rb.__aRevObserved = true;
        dlog('attachReviewsBodyObserver: attached to #reviewsBody');
        new MutationObserver(obsCallback('observer:#reviewsBody children')).observe(rb, { childList: true });
      };
      attachReviewsBodyObserver();
      // Re-attach when AReviews recreates `#reviewsBody` (full XHR replace
      // of `#az_reviews.innerHTML`). The subtree-watch is necessary because
      // `#reviewsBody` is nested several levels deep inside the response,
      // not a direct child of `#az_reviews`.
      //
      // Filter for the SPECIFIC mutation we care about — an added node
      // whose id is `reviewsBody` (or that contains one). Without the
      // filter, every descendant mutation (each lazy-loaded image, each
      // masonry span style assignment, etc. — hundreds during initial
      // render) would invoke `attachReviewsBodyObserver`. The flag check
      // inside is cheap, but skipping the call entirely is cheaper.
      new MutationObserver((mutations) => {
        for (const m of mutations) {
          for (const n of m.addedNodes) {
            if (n.nodeType !== 1) continue;
            if (n.id === 'reviewsBody' || n.querySelector?.('#reviewsBody')) {
              attachReviewsBodyObserver();
              return;
            }
          }
        }
      }).observe(azReviews, { childList: true, subtree: true });
    }

    // (b) Espera a contagem chegar, seja qual for o markup do tema.
    // Desconecta assim que os dados aparecem, então o subtree não pesa.
    if (azReviews && getReviewsCount() === null) {
      const dataWaiter = new MutationObserver(() => {
        if (getReviewsCount() === null) return;
        dataWaiter.disconnect();
        dlog('dataWaiter: contagem =', getReviewsCount());
        scheduleRun('waiter:contagem');
      });
      dataWaiter.observe(azReviews, { childList: true, subtree: true, characterData: true });
    } else {
      dlog('init: contagem já disponível =', getReviewsCount());
    }

    // (c) AReviews's `injectProductRating` inserts orphan containers as
    // siblings of `.areviews_header_stars`. The CSS ownership invariant
    // already hides them, but observing lets us schedule a run() to
    // refresh the seeded container's signature if AReviews's pill arrives
    // with newer data than ours.
    //
    // Idempotent attach: marks each block with `__aRevObserved` so re-attach
    // is a no-op. Called both at init and on `shopify:section:load` (theme
    // editor block reorder/add) so observers don't go stale on replaced nodes.
    const attachTitleBadgeObservers = () => {
      document.querySelectorAll(AREV_TITLE_BLOCK_SEL).forEach((b) => {
        if (b.__aRevObserved) return;
        b.__aRevObserved = true;
        new MutationObserver(obsCallback('observer:AreviewsTitleRating children')).observe(b, { childList: true });
      });
    };
    attachTitleBadgeObservers();

    // Theme-editor lifecycle: when the merchant adds/moves/edits a block,
    // Shopify dispatches `shopify:section:load` (or its `:reorder` variant).
    // The section subtree is replaced with new nodes — our observers point
    // at the destroyed nodes. Re-attach + re-run.
    document.addEventListener('shopify:section:load', () => {
      attachTitleBadgeObservers();
      scheduleRun('event:shopify:section:load');
    });
    document.addEventListener('shopify:block:reorder', () => scheduleRun('event:shopify:block:reorder'));
    document.addEventListener('shopify:block:select', () => scheduleRun('event:shopify:block:select'));

    // Fallback: if `#totalReviews` never arrives within AREV_DATA_TIMEOUT_MS,
    // schedule one more run() right at the deadline so applyTitleBadge falls
    // through and renders the "Novo!" empty-state pill instead of leaving
    // the skeleton pulsing forever.
    setTimeout(() => {
      if (AREV_DEBUG) {
        const count = getReviewsCount();
        const reviewsBody = document.getElementById('reviewsBody');
        const sources = reviewsBody?.querySelectorAll('.callout.areviews_slide_image_details').length ?? 0;
        const skeletonStuck = !!document.querySelector(reviewsRootSel + ' .aRev-holder--loading');
        if (skeletonStuck) {
          let reason = 'unknown';
          if (count === null) reason = '#totalReviews never populated — AReviews data XHR likely did not land (check Network tab for app XHR, CSP errors, or app deactivation)';
          else if (count > 0 && !reviewsBody) reason = '#totalReviews populated (count=' + count + ') but #reviewsBody never appeared';
          else if (count > 0 && sources === 0) reason = '#reviewsBody present but no .callout.areviews_slide_image_details cards inside (selector drift in this AReviews version?)';
          dwarn('DEADLINE REACHED (' + AREV_DATA_TIMEOUT_MS + 'ms) — skeleton STILL VISIBLE. Reason: ' + reason);
          debugSnapshot('deadline-stuck');
        } else {
          dlog('deadline reached — skeleton already cleared, all good');
        }
      }
      /* FAIL-OPEN: passou o deadline sem assumir o controle → devolve o
         markup nativo em vez de deixar a seção invisível para sempre. */
      const azEl = document.querySelector(reviewsRootSel);
      if (azEl && !azEl.classList.contains('aRev-rebuilt')
               && !azEl.classList.contains('aRev-empty')) {
        azEl.classList.add('aRev-fallback-native');
        console.warn('[areviews-custom] FAIL-OPEN — exibindo markup nativo do AReviews.');
      }

      scheduleRun('deadline:' + AREV_DATA_TIMEOUT_MS + 'ms');
    }, AREV_DATA_TIMEOUT_MS + 16);

    // Filter mutations: only run promote when a <link> or <style> was added.
    // GTM, analytics and app embeds inject many <script> tags into <head>;
    // skipping them spares dozens of pointless invocations per nav.
    const headObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const n of m.addedNodes) {
          if (n.nodeType !== 1) continue;
          if (n.tagName === 'LINK' || n.tagName === 'STYLE') {
            promoteCustomStylesheet();
            return;
          }
        }
      }
    });
    headObserver.observe(document.head, { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================
   AReviews card-overlay rating transformer — Linda Casa parity
   ============================================================
   Replaces the app-native 5-star FontAwesome markup that
   collection_rating.js injects into `.areviews_product_item[data-variant="card-inline"]`
   with a single-star Linda Casa-style badge:

     <div class="aRev-card-badge" data-has-reviews="true|false">
       <svg class="aRev-card-star">...</svg>
       <span class="aRev-card-badge__count">5 | Novo!</span>
     </div>

   Runs as a single delegated MutationObserver on document.body
   (not per-card) so N product cards on a page = 1 observer,
   not N observers. Throttled via requestAnimationFrame so batched
   app injections only trigger one transform pass per frame.
   ============================================================ */
(function () {
  if (window.__aRevCardBadgeInit) return;
  window.__aRevCardBadgeInit = true;

  // Shared star path (Linda Casa SVG geometry, viewBox 14x13).
  var STAR_PATH =
    'M7.13557 10.5846C6.92869 10.437 6.64896 10.437 6.44208 10.5846L3.26702 12.8498' +
    'C3.11469 12.9585 2.91895 12.9892 2.73983 12.9327C2.42896 12.8345 2.25773 12.5067 2.35738 12.2005' +
    'L3.55886 8.50882C3.63805 8.26549 3.54691 7.99973 3.33412 7.85355L0.252948 5.73677' +
    'C0.0944532 5.62789 0 5.44957 0 5.25923C0 4.93768 0.264648 4.67701 0.591107 4.67701H4.34417' +
    'C4.59811 4.67701 4.82368 4.51725 4.90452 4.28014L6.22848 0.397028C6.28741 0.224174 6.42516 0.0884988 6.60065 0.03045' +
    'C6.91012 -0.0719175 7.24525 0.0922051 7.34918 0.397028L8.67313 4.28014C8.75398 4.51725 8.97954 4.67701 9.23349 4.67701' +
    'H12.9866C13.1798 4.67701 13.3608 4.77004 13.4714 4.92616C13.6581 5.18989 13.5925 5.55282 13.3247 5.73677' +
    'L10.2435 7.85355C10.0308 7.99973 9.93961 8.26549 10.0188 8.50882L11.2203 12.2005' +
    'C11.2777 12.3769 11.2464 12.5697 11.1361 12.7198C10.9446 12.9802 10.575 13.0384 10.3106 12.8498L7.13557 10.5846Z';
  var STAR_FILL = '#FFCC00';
  var STAR_EMPTY = 'rgba(153, 153, 153, 0.3)';

  var gradCounter = 0;

  /**
   * Parse AReviews' injected rating markup and extract (rating, count).
   * The app renders 5 `<i>` elements with:
   *   .fas.fa-star       → filled star
   *   .far.fa-star       → empty star
   *   .fas.fa-star-half-alt (or similar) → half star
   * followed by a `<span>` with text like "3 avaliações" or "0 avaliação".
   */
  function parseAReviewsRating(rootEl) {
    var stars = rootEl.querySelectorAll('i.fa-star, i.fa-star-half-alt, i.fa-star-half');
    var filled = 0;
    var half = 0;
    for (var i = 0; i < stars.length; i++) {
      var el = stars[i];
      if (el.classList.contains('fa-star-half-alt') || el.classList.contains('fa-star-half')) {
        half++;
      } else if (el.classList.contains('fas')) {
        filled++;
      }
    }
    var rating = filled + half * 0.5;
    var span = rootEl.querySelector('span');
    var countText = span ? span.textContent : '';
    var countMatch = countText.match(/(\d+)/);
    var count = countMatch ? parseInt(countMatch[1], 10) : 0;
    return { rating: rating, count: count };
  }

  /**
   * Build the Boticário-style 5-star rating widget for the given rating.
   * The widget is a 2-element DOM (container + stars div); colors and fill
   * percentage are driven entirely by CSS custom properties + ::before/::after
   * mask layers (see `.aRev-card-rating` in areviews-custom.css).
   *
   * Accessibility mirrors Boticário: container holds `aria-label` ("Nota X de 5")
   * + `title` ("N avaliações"), inner stars div is `aria-hidden`. Empty state
   * (count = 0) renders 5 grey stars only — no text fallback.
   */
  function buildBoticarioRating(rating, count) {
    var hasReviews = count > 0;
    // Map 0–5 rating to 0–100% fill width for the orange overlay layer.
    // Clamp at 100 so e.g. an erroneous 5.2 rating still renders 5 full stars.
    var fillPercent = hasReviews ? Math.min((rating / 5) * 100, 100) : 0;

    var ratingLabel = rating.toFixed(1).replace('.', ','); // "4,5" pt-BR
    var countLabel = count + ' ' + (count === 1 ? 'avaliação' : 'avaliações');

    var container = document.createElement('div');
    container.className = 'aRev-card-rating';
    container.setAttribute('data-has-reviews', hasReviews ? 'true' : 'false');
    container.style.setProperty('--aRev-fill-percent', fillPercent + '%');
    if (hasReviews) {
      container.setAttribute('aria-label', 'Nota ' + ratingLabel + ' de 5 (' + countLabel + ')');
      container.setAttribute('title', countLabel);
    } else {
      container.setAttribute('aria-label', 'Sem avaliações');
      container.setAttribute('title', 'Sem avaliações');
    }

    var stars = document.createElement('div');
    stars.className = 'aRev-card-rating__stars';
    stars.setAttribute('aria-hidden', 'true');
    container.appendChild(stars);

    return container;
  }

  /**
   * Replace AReviews' 5-star markup inside a container with our Boticário widget.
   * Idempotent: if the container already holds `.aRev-card-rating`, no-op.
   */
  function transformContainer(container) {
    if (!container || container.querySelector('.aRev-card-rating')) return;
    var native = container.querySelector('.areviews_rating');
    if (!native) return;
    var parsed = parseAReviewsRating(native);
    var widget = buildBoticarioRating(parsed.rating, parsed.count);
    // Replace native markup atomically — container ends up with a single
    // `.aRev-card-rating` child, the native 5-star markup is discarded.
    container.replaceChildren(widget);
  }

  /**
   * Scan document for card-overlay containers that already have native
   * AReviews markup (initial DOM + replay cases where the observer missed
   * the injection).
   */
  function scan() {
    var containers = document.querySelectorAll(
      '.areviews_product_item[data-variant="card-inline"]'
    );
    for (var i = 0; i < containers.length; i++) transformContainer(containers[i]);
  }

  // Debounce scans with rAF — app injections during a single frame only
  // trigger one transform pass.
  var pending = false;
  function scheduleScan() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      scan();
    });
  }

  function initCardBadges() {
    scan();
    // Observe body for subtree mutations. `.areviews_rating` nodes are
    // inserted by https://areviewsapp.com/js_codes/collection_rating.js
    // into our card-overlay containers; when they appear we transform.
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.addedNodes && m.addedNodes.length) {
          scheduleScan();
          return;
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCardBadges);
  } else {
    initCardBadges();
  }
})();
